import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Btn, Modal, Input, Select, Badge, Textarea } from "@/components/UI";
import { Plus, Wallet, TrendingUp, TrendingDown, PieChart, Package, Users, DollarSign, Search, Edit2, Trash2, X, PlusCircle, Percent, Clock, AlertTriangle, ArrowRightLeft, CheckCircle2, RotateCcw, Settings, Pencil, Handshake, Banknote, Eye, Download, Printer, History } from "lucide-react";
import { previewPdf as previewPdfV2, downloadPdf as downloadPdfV2, printPdf as printPdfV2 } from "@/lib/pdfActions";
import NewMovementModal from "@/components/NewMovementModal";
import FaturaMerkezi from "./FaturaMerkezi";
import VergiHesabiTab from "./VergiHesabiTab";
import { FileText } from "lucide-react";
import { openPdfPreview } from "@/lib/pdfPreview";
import SmartSuggestInput from "@/components/SmartSuggestInput";
import { todayTR } from "@/lib/date";

/* ============================================================
   MUHASEBE — 3 sekme (Bakiye Takip / Mali Analiz / Gelir Gider)
   Aynı finansal işlem tek yerde tutulur; view-time birleşimle
   diğer sekmelerde görüntülenir. Duplicate kayıt yok.
============================================================ */

const money = (n, cur = "TRY") => `${(Number(n || 0)).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur === "TRY" ? "TL" : cur === "EUR" ? "€" : cur}`;
// iter130 — Kısaltma: müşteri/tedarikçi isimlerinin sadece ilk iki kelimesi (Borç/Alacak tablolarında)
const firstTwo = (name) => {
  const s = String(name || "").trim();
  if (!s) return "—";
  const parts = s.split(/\s+/);
  return parts.length <= 2 ? s : parts.slice(0, 2).join(" ") + "…";
};
const moneyEur = (n) => money(n, "EUR");
const moneyTry = (n) => money(n, "TRY");
const todayStr = () => todayTR();

const PM_LABEL = { CASH: "Nakit", CREDIT_CARD: "Kredi Kartı", CHECK: "Çek", BANK: "Banka / Havale", OTHER: "Diğer" };
const SRC_LABEL = { customer_payment: "Müşteri Ödemesi", manual: "Manuel", sales_order: "Satış Siparişi" };

/* iter262 — PDF butonları için ortak Önizle/İndir/Yazdır trio bileşeni.
   Her alt-sekmenin filtre barına inline olarak yerleştirilir. */
function PdfActions({ url, filename, label, color = "indigo", testidBase }) {
  const colorCls = {
    indigo: "bg-indigo-600 hover:bg-indigo-700",
    amber: "bg-amber-600 hover:bg-amber-700",
    emerald: "bg-emerald-600 hover:bg-emerald-700",
    cyan: "bg-cyan-600 hover:bg-cyan-700",
    blue: "bg-blue-600 hover:bg-blue-700",
    purple: "bg-purple-600 hover:bg-purple-700",
    rose: "bg-rose-600 hover:bg-rose-700",
    yellow: "bg-yellow-600 hover:bg-yellow-700",
    fuchsia: "bg-fuchsia-600 hover:bg-fuchsia-700",
  }[color] || "bg-indigo-600 hover:bg-indigo-700";
  return (
    <div className="inline-flex items-center gap-1 border rounded-md px-1.5 py-1 bg-white" title={`${label} PDF`}>
      {label && <span className="text-[10px] font-semibold text-slate-600 mr-1">{label}</span>}
      <button title={`${label} Önizle`} data-testid={`${testidBase}-preview`}
        onClick={() => previewPdfV2(url, filename)}
        className={`inline-flex items-center justify-center w-7 h-7 rounded text-white ${colorCls}`}><Eye size={14}/></button>
      <button title={`${label} İndir`} data-testid={`${testidBase}-download`}
        onClick={() => downloadPdfV2(url, filename)}
        className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800"><Download size={14}/></button>
      <button title={`${label} Yazdır`} data-testid={`${testidBase}-print`}
        onClick={() => printPdfV2(url, filename)}
        className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800"><Printer size={14}/></button>
    </div>
  );
}

export function FinancePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams] = useSearchParams();
  // iter108: Dashboard'daki "Yaklaşan Tedarikçi Vadeleri" widget'ından
  // gelen ?tab=supplier_balance query param'ını respekt et (deep-link).
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return ["balance", "supplier_balance", "analysis", "cashflow", "debts", "vade_maliyeti", "factoring", "fatura_merkezi", "vergi_hesabi"].includes(t) ? t : "balance";
  })();
  const [tab, setTab] = useState(initialTab);
  const [newMovOpen, setNewMovOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const tabs = [
    { k: "balance", l: "Müşteri Bakiye Takip", Icon: Users },
    { k: "supplier_balance", l: "Tedarikçi Bakiye Takip", Icon: Package },
    { k: "analysis", l: "Mali Analiz", Icon: PieChart },
    { k: "cashflow", l: "Gelir Gider", Icon: Wallet },
    { k: "debts", l: "Borç / Alacak Takibi", Icon: DollarSign },
    // iter121 Faktoring — simple factoring tracking using existing cheques.
    { k: "factoring", l: "Faktoring", Icon: Handshake },
    // iter129 Fatura Merkezi — INVOICE-ONLY multi-provider integration
    ...(isAdmin ? [{ k: "fatura_merkezi", l: "Fatura Merkezi", Icon: FileText }] : []),
    // iter101 Vade Maliyeti — Admin-only analytical financing cost.
    ...(isAdmin ? [{ k: "vade_maliyeti", l: "Vade Maliyeti", Icon: Percent }] : []),
    { k: "vergi_hesabi", l: "Vergi Hesabı", Icon: Percent },
  ];
  return (
    <div>
      <div className="flex justify-between items-start">
        <PageHeader title="Muhasebe" description="Bakiye takip, mali analiz ve gelir–gider akışı" />
        <div className="flex items-center gap-2 mt-2">
          {/* iter262 — PDF butonları alt-sekme filtre barlarına taşındı;
              header yalnızca aksiyon butonları içerir. */}
          <Btn
            onClick={() => setNewMovOpen(true)}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold px-4 py-2 shadow-lg hover:shadow-xl"
            data-testid="muh-new-movement">
            <PlusCircle size={16}/> Yeni Hareket
          </Btn>
        </div>
      </div>
      <div className="flex gap-1 mb-4 border-b border-slate-200 flex-wrap">
        {tabs.map(({ k, l, Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`muh-tab-${k}`}
            className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 border-b-2 -mb-px transition ${tab === k ? "border-emerald-600 text-emerald-700 bg-emerald-50/40" : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}>
            <Icon size={14}/> {l}
          </button>
        ))}
      </div>
      {tab === "balance" && <BalanceTrackingTab key={`bal-${reloadKey}`} />}
      {tab === "supplier_balance" && <SupplierBalanceTrackingTab key={`sbal-${reloadKey}`} />}
      {tab === "analysis" && <FinancialAnalysisTab />}
      {tab === "cashflow" && <CashflowTab key={`cf-${reloadKey}`} />}
      {tab === "debts" && <DebtsCreditsTab />}
      {tab === "fatura_merkezi" && <FaturaMerkezi />}
      {tab === "vade_maliyeti" && isAdmin && <VadeMaliyetiTab key={`vm-${reloadKey}`} />}
      {tab === "vergi_hesabi" && <VergiHesabiTab key={`vh-${reloadKey}`} />}
      {tab === "factoring" && <FactoringTab key={`fx-${reloadKey}`} />}
      <NewMovementModal open={newMovOpen} onClose={() => setNewMovOpen(false)} onSaved={() => setReloadKey(k => k + 1)}/>
    </div>
  );
}

/* ================== iter99 Paket 03.2: TEDARİKÇİ BAKİYE TAKİP ================== */
function SupplierBalanceTrackingTab() {
  const [data, setData] = useState({ suppliers: [], summary: {} });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [period, setPeriod] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const [selected, setSelected] = useState(null);
  const [newPurchaseOpen, setNewPurchaseOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const params = { period };
      if (period === "range") { params.start = range.start; params.end = range.end; }
      const r = await api.get("/accounting/supplier-balance-tracking", params);
      setData(r);
    } catch (e) { console.error(e); }
  }, [period, range]);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => {
    let arr = [...(data.suppliers || [])];
    if (q.trim()) {
      const qt = q.toLocaleLowerCase("tr");
      arr = arr.filter(r => (r.supplier_name || "").toLocaleLowerCase("tr").includes(qt));
    }
    if (status === "debt") arr = arr.filter(r => r.balance > 0);
    else if (status === "zero") arr = arr.filter(r => Math.abs(r.balance) < 0.01);
    else if (status === "credit") arr = arr.filter(r => r.balance < 0);
    arr.sort((a, b) => {
      if (sortBy === "balance_desc") return b.balance - a.balance;
      if (sortBy === "balance_asc") return a.balance - b.balance;
      if (sortBy === "recent") return (b.last_transaction_date || "").localeCompare(a.last_transaction_date || "");
      return (a.supplier_name || "").localeCompare(b.supplier_name || "", "tr");
    });
    return arr;
  }, [data.suppliers, q, status, sortBy]);
  const fs = useMemo(() => ({
    total_payable: rows.filter(r => r.balance > 0).reduce((s, r) => s + r.balance, 0),
    total_purchases: rows.reduce((s, r) => s + r.total_purchases, 0),
    total_payments: rows.reduce((s, r) => s + r.total_payments, 0),
    creditor_count: rows.filter(r => r.balance > 0).length,
  }), [rows]);
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Toplam Borç" value={money(fs.total_payable)} color="rose" icon={DollarSign} testid="sbt-stat-payable"/>
        <StatCard label="Toplam Alım" value={money(fs.total_purchases)} color="indigo" icon={TrendingUp} testid="sbt-stat-purchases"/>
        <StatCard label="Toplam Ödeme" value={money(fs.total_payments)} color="emerald" icon={Wallet} testid="sbt-stat-payments"/>
        <StatCard label="Borçlu Olduğumuz" value={`${fs.creditor_count} tedarikçi`} color="amber" icon={Package} testid="sbt-stat-creditors"/>
      </div>
      <div className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 mb-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pl-0.5">Arama</label>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-2.5 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Tedarikçi..." className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded" data-testid="sbt-search"/>
          </div>
        </div>
        <Select label="Bakiye Durumu" value={status} onChange={e => setStatus(e.target.value)} data-testid="sbt-status">
          <option value="all">Tümü</option>
          <option value="debt">Borcumuz Olan</option>
          <option value="zero">Bakiye Sıfır</option>
          <option value="credit">Fazla Ödeme Yaptığımız</option>
        </Select>
        <Select label="Sıralama" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="name">Tedarikçi Adı</option>
          <option value="balance_desc">En Yüksek Borç</option>
          <option value="balance_asc">En Düşük Borç</option>
          <option value="recent">Son İşlem Tarihi</option>
        </Select>
        <Btn
          onClick={() => setNewPurchaseOpen(true)}
          className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white font-bold px-3 py-1.5 shadow"
          data-testid="sbt-new-purchase-btn"
        >
          <PlusCircle size={14}/> Yeni Alım/Borç
        </Btn>
        <Select label="Dönem" value={period} onChange={e => setPeriod(e.target.value)} data-testid="sbt-period">
          <option value="all">Tümü</option><option value="today">Bugün</option><option value="week">Bu Hafta</option>
          <option value="month">Bu Ay</option><option value="year">Bu Yıl</option><option value="range">Tarih Aralığı</option>
        </Select>
        {period === "range" && (<>
          <Input label="Başlangıç" type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })}/>
          <Input label="Bitiş" type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })}/>
        </>)}
        {/* iter262/263 — R21 Tedarikçi Bakiye PDF trio (filtreye duyarlı) */}
        <PdfActions url={`/api/accounting/supplier-balance-tracking/pdf?period=${period}${period==="range"&&range.start?`&start=${range.start}`:""}${period==="range"&&range.end?`&end=${range.end}`:""}`} filename="Tedarikci_Bakiye.pdf" label="Tedarikçi Bakiye PDF" color="purple" testidBase="muh-r21"/>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="sbt-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Tedarikçi Adı</th>
              <th className="text-left px-3 py-2">Son İşlem</th>
              <th className="text-right px-3 py-2">Toplam Alım</th>
              <th className="text-right px-3 py-2">Toplam Ödeme</th>
              <th className="text-right px-3 py-2">Kalan Borç</th>
              <th className="text-left px-3 py-2">Not</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Kayıt yok.</td></tr>}
            {rows.map(r => (
              <tr key={r.supplier_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(r)} data-testid={`sbt-row-${r.supplier_id}`}>
                <td className="px-3 py-2 font-semibold text-indigo-700">
                  <div className="flex items-center gap-1.5">
                    <span>{r.supplier_name}</span>
                    {r.has_due_warning && <DueWarningIcon supplier={r} />}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono">{r.last_transaction_date || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{money(r.total_purchases)}</td>
                <td className="px-3 py-2 text-right font-mono">{money(r.total_payments)}</td>
                <td className={`px-3 py-2 text-right font-mono font-semibold ${r.balance > 0 ? "text-rose-700" : r.balance < 0 ? "text-emerald-700" : "text-slate-500"}`} data-testid={`sbt-balance-${r.supplier_id}`}>{money(r.balance)}</td>
                <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[180px]" title={r.note}>{r.note || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && <SupplierTxnModal supplier={selected} onClose={() => { setSelected(null); load(); }}/>}
      {newPurchaseOpen && (
        <NewSupplierPurchaseModal
          onClose={() => setNewPurchaseOpen(false)}
          onSaved={() => { setNewPurchaseOpen(false); load(); }}
        />
      )}
    </div>
  );
}

/* iter104 spec §10-13: due-date warning icon + popover — kırmızı ünlem +
   popover ile açık borçların kalan tutar + gün bilgisi. */
function DueWarningIcon({ supplier }) {
  const [open, setOpen] = useState(false);
  const warns = supplier.due_warnings || [];
  return (
    <span className="relative inline-block" onMouseLeave={() => setOpen(false)}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        onMouseEnter={() => setOpen(true)}
        className="text-rose-600 hover:text-rose-800 font-bold text-sm"
        data-testid={`sbt-warn-${supplier.supplier_id}`}
        title="Vadesi yaklaşan borçlar"
      >
        <AlertTriangle size={14} className="inline" fill="currentColor" fillOpacity={0.15}/>
      </button>
      {open && warns.length > 0 && (
        <div className="absolute z-50 top-5 left-0 bg-white border border-rose-300 rounded shadow-lg p-2 text-xs w-72" onClick={(e) => e.stopPropagation()} data-testid={`sbt-warn-popover-${supplier.supplier_id}`}>
          <div className="text-[10px] uppercase font-bold text-rose-700 mb-1">Vadesi Yaklaşan Borçlar</div>
          <ul className="space-y-1">
            {warns.map(w => (
              <li key={w.purchase_id} className={`p-1 rounded ${w.is_overdue ? "bg-rose-100" : "bg-amber-50"}`}>
                <b className="font-mono">{money(w.remaining)}</b> TRY
                <span className="mx-1">·</span>
                <span className="text-slate-600">{w.due_date}</span>
                <span className="mx-1">·</span>
                <span className={w.is_overdue ? "font-bold text-rose-800" : "text-amber-800"}>
                  {w.is_overdue ? `Vadesi Geçti (${Math.abs(w.days_left)} gün gecikti)` : `${w.days_left} gün kaldı`}
                </span>
                {w.note && <div className="text-[10px] text-slate-500 italic mt-0.5">{w.note}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  );
}

/* ============================================================
   iter103 Fix Pkg — Manuel Tedarikçi Borç/Alım kaydı modalı
   Payment method CHECK seçilirse:
     · "Portföyden mevcut çek seç" → aynı cheque_id ciro edilir
     · veya manuel yeni çek bilgisi
   Alanlar: Tedarikçi, Tarih, Tutar, Vade Tarihi, Ödeme Şekli, Not
============================================================ */
function NewSupplierPurchaseModal({ onClose, onSaved, presetSupplier = null, presetType = "PURCHASE", outstandingPurchases = [] }) {
  const [suppliers, setSuppliers] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [supplierQ, setSupplierQ] = useState("");
  const [dueManuallyEdited, setDueManuallyEdited] = useState(false);
  // iter119: transaction_type artık kullanıcı seçimi — modal hem Alım/Borç
  // hem de Ödeme ekleyebilir. presetSupplier verildiyse tedarikçi kilitli
  // ve otomatik doldurulur.
  const [txnType, setTxnType] = useState(presetType === "PAYMENT" ? "PAYMENT" : "PURCHASE");
  // iter120 — Hızlı Ödeme Alokasyonu: preset supplier + PAYMENT modunda,
  // kalan borçlardan birine yaz seçeneği. Kullanıcı seçmezse allocation
  // yapılmaz (backend allocated_to_liability_id opsiyonel).
  const [allocateTo, setAllocateTo] = useState("");
  const [form, setForm] = useState({
    supplier_id: presetSupplier?.id || presetSupplier?.supplier_id || "",
    transaction_date: todayTR(),
    amount: "",
    due_date: "",
    payment_method: "CASH",
    note: "",
    cheque_mode: "existing",
    linked_customer_cheque_id: "",
    check_number: "",
    bank_name: "",
    check_due_date: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get("/suppliers"),
          api.get("/accounting/cheques/portfolio"),
        ]);
        setSuppliers(s || []);
        setPortfolio(p?.cheques || []);
      } catch (e) { setErr(e?.message || "Veri yüklenemedi"); }
    })();
  }, []);

  const filteredSuppliers = useMemo(() => {
    if (!supplierQ.trim()) return suppliers.slice(0, 25);
    const qt = supplierQ.toLocaleLowerCase("tr");
    return suppliers
      .filter(s => (s.name || "").toLocaleLowerCase("tr").includes(qt))
      .slice(0, 25);
  }, [suppliers, supplierQ]);

  const selectedSupplier = presetSupplier
    ? (suppliers.find(s => s.id === (presetSupplier.id || presetSupplier.supplier_id)) ||
        { id: presetSupplier.id || presetSupplier.supplier_id, name: presetSupplier.name || presetSupplier.supplier_name })
    : suppliers.find(s => s.id === form.supplier_id);

  // iter106 spec §6-8: auto-compute due_date from supplier.payment_term_days.
  // Manual override tracked in `dueManuallyEdited` — once user edits due_date,
  // stop overwriting on supplier/date changes.
  useEffect(() => {
    if (dueManuallyEdited) return;
    const term = selectedSupplier?.payment_term_days;
    if (!selectedSupplier || term == null || term === "" || !form.transaction_date) return;
    const termInt = parseInt(term, 10);
    if (isNaN(termInt) || termInt < 0) return;
    const base = new Date(form.transaction_date);
    if (isNaN(base.getTime())) return;
    base.setDate(base.getDate() + termInt);
    const nextDue = base.toISOString().slice(0, 10);
    if (nextDue !== form.due_date) setForm(f => ({ ...f, due_date: nextDue }));
  }, [selectedSupplier, form.transaction_date, dueManuallyEdited]);

  const submit = async () => {
    setErr("");
    if (!form.supplier_id) { setErr("Tedarikçi seçin"); return; }
    if (!form.amount || Number(form.amount) <= 0) { setErr("Tutar 0'dan büyük olmalı"); return; }
    if (!form.transaction_date) { setErr("Tarih zorunlu"); return; }
    const payload = {
      supplier_id: form.supplier_id,
      transaction_type: txnType,
      transaction_date: form.transaction_date,
      amount: Number(form.amount),
      payment_method: form.payment_method,
      due_date: txnType === "PURCHASE" ? (form.due_date || null) : null,
      note: form.note || null,
    };
    // iter120: Hızlı Ödeme Alokasyonu — kullanıcı belirli bir borca yaz dediyse
    // backend allocated_to_liability_id ile eşleştirir. Overpayment kontrolü backend'de.
    if (txnType === "PAYMENT" && allocateTo) {
      payload.allocated_to_liability_id = allocateTo;
    }
    if (form.payment_method === "CHECK") {
      if (form.cheque_mode === "existing") {
        if (!form.linked_customer_cheque_id) { setErr("Portföyden bir çek seçin"); return; }
        payload.linked_customer_cheque_id = form.linked_customer_cheque_id;
      } else {
        if (!form.check_number || !form.bank_name || !form.check_due_date) {
          setErr("Yeni çek için Çek No, Banka ve Vade zorunlu"); return;
        }
        payload.check_number = form.check_number;
        payload.bank_name = form.bank_name;
        payload.check_due_date = form.check_due_date;
      }
    }
    setBusy(true);
    try {
      await api.post("/accounting/supplier-transactions", payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Kayıt başarısız");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title={presetSupplier ? `YENİ HAREKET — ${presetSupplier.name || presetSupplier.supplier_name}` : "YENİ TEDARİKÇİ BORÇ / ALIM KAYDI"} size="lg">
      <div className="space-y-3">
        <div className="bg-indigo-50/60 border border-indigo-200 rounded p-2 text-[11px] text-indigo-900">
          Manuel borç kaydı — Satın Alma modülü artık otomatik tedarikçi bakiyesi oluşturmaz. Bu kayıt merkezi supplier_transactions tablosunda saklanır.
        </div>

        {/* iter119 — İşlem türü seçici (Alım/Borç veya Ödeme) */}
        <div className="flex items-center gap-2" data-testid="sbt-new-type-group">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">İşlem Türü *</label>
          <div className="flex gap-1">
            <button
              onClick={() => setTxnType("PURCHASE")}
              data-testid="sbt-new-type-purchase"
              className={`text-xs px-3 py-1.5 rounded border font-semibold transition ${
                txnType === "PURCHASE"
                  ? "bg-rose-100 border-rose-400 text-rose-900"
                  : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
              }`}
            >Alım / Borç</button>
            <button
              onClick={() => setTxnType("PAYMENT")}
              data-testid="sbt-new-type-payment"
              className={`text-xs px-3 py-1.5 rounded border font-semibold transition ${
                txnType === "PAYMENT"
                  ? "bg-emerald-100 border-emerald-400 text-emerald-900"
                  : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
              }`}
            >Ödeme</button>
          </div>
        </div>

        {/* Supplier — presetSupplier varsa kilitli, yoksa autocomplete */}
        {presetSupplier ? (
          <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs" data-testid="sbt-new-supplier-locked">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-0.5">Tedarikçi</span>
            <span className="font-semibold text-indigo-900">{presetSupplier.name || presetSupplier.supplier_name}</span>
            {selectedSupplier?.payment_term_days != null && (
              <span className="ml-2 text-[10px] text-slate-500">· Vade: {selectedSupplier.payment_term_days} gün</span>
            )}
          </div>
        ) : (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tedarikçi *</label>
            <input
              value={selectedSupplier ? selectedSupplier.name : supplierQ}
              onChange={e => { setSupplierQ(e.target.value); setForm(f => ({ ...f, supplier_id: "" })); }}
              placeholder="Tedarikçi ara..."
              className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded"
              data-testid="sbt-new-supplier-input"
            />
            {!form.supplier_id && supplierQ && (
              <div className="border border-slate-200 rounded mt-1 max-h-40 overflow-y-auto bg-white">
                {filteredSuppliers.length === 0 && <div className="text-xs text-slate-400 p-2">Eşleşme yok</div>}
                {filteredSuppliers.map(s => (
                  <button
                    key={s.id}
                    onClick={() => { setForm(f => ({ ...f, supplier_id: s.id })); setSupplierQ(""); }}
                    className="block w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50"
                    data-testid={`sbt-new-supplier-opt-${s.id}`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Date + amount + due date + method */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Input label="Tarih *" type="date" value={form.transaction_date}
                 onChange={e => setForm({ ...form, transaction_date: e.target.value })}
                 data-testid="sbt-new-date"/>
          <Input label="Tutar (TRY) *" type="number" step="0.01" value={form.amount}
                 onChange={e => setForm({ ...form, amount: e.target.value })}
                 data-testid="sbt-new-amount"/>
          <Input label={txnType === "PURCHASE" ? "Vade Tarihi (Borç)" : "Vade Tarihi"} type="date" value={form.due_date}
                 onChange={e => { setDueManuallyEdited(true); setForm({ ...form, due_date: e.target.value }); }}
                 disabled={txnType === "PAYMENT"}
                 data-testid="sbt-new-due-date"/>
        </div>

        {/* iter120 — Hızlı Ödeme Alokasyonu: PAYMENT + preset supplier + outstanding purchases */}
        {txnType === "PAYMENT" && presetSupplier && outstandingPurchases.length > 0 && (
          <div className="bg-emerald-50/60 border border-emerald-200 rounded p-2" data-testid="sbt-new-alloc-block">
            <label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold block mb-1">
              Kalan Borçlardan Hangisine Yaz? <span className="text-slate-400 normal-case">(opsiyonel)</span>
            </label>
            <select
              value={allocateTo}
              onChange={e => {
                setAllocateTo(e.target.value);
                // Otomatik olarak seçilen borcun kalan tutarını Tutar alanına doldur.
                const p = outstandingPurchases.find(x => x.id === e.target.value);
                if (p && (!form.amount || Number(form.amount) === 0)) {
                  setForm(f => ({ ...f, amount: Number(p.remaining).toFixed(2) }));
                }
              }}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
              data-testid="sbt-new-alloc-select"
            >
              <option value="">— Genel ödeme (belirli borca yazma) —</option>
              {outstandingPurchases.map(p => (
                <option key={p.id} value={p.id}>
                  {(p.transaction_date || "").slice(0, 10)} · Alım {money(p.amount)} · Kalan {money(p.remaining)}{p.due_date ? ` · Vade ${p.due_date}` : ""}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-slate-500 italic mt-1">
              Seçilen alım kaydına ödeme allocate edilir; overpayment backend'de bloklanır. Tutar alanı otomatik doldurulur.
            </div>
          </div>
        )}
        <Select label="Ödeme Şekli *" value={form.payment_method}
                onChange={e => setForm({ ...form, payment_method: e.target.value })}
                data-testid="sbt-new-pm">
          <option value="CASH">Nakit</option>
          <option value="CREDIT_CARD">Kredi Kartı</option>
          <option value="MAIL_ORDER">Kredi Kartı - Mail Order</option>
          <option value="CHECK">Çek</option>
        </Select>

        {/* Cheque sub-form */}
        {form.payment_method === "CHECK" && (
          <div className="bg-amber-50/60 border border-amber-300 rounded p-3">
            <div className="text-xs font-bold text-amber-900 mb-2 flex items-center gap-2">
              <ArrowRightLeft size={14}/> Çek Detayı
            </div>
            <div className="flex gap-2 mb-2">
              <label className="text-xs flex items-center gap-1">
                <input type="radio" checked={form.cheque_mode === "existing"}
                       onChange={() => setForm({ ...form, cheque_mode: "existing" })}
                       data-testid="sbt-new-cheque-mode-existing"/>
                Portföyden çek seç (aynı ID ile tedarikçiye ciro edilir)
              </label>
              <label className="text-xs flex items-center gap-1">
                <input type="radio" checked={form.cheque_mode === "manual"}
                       onChange={() => setForm({ ...form, cheque_mode: "manual" })}
                       data-testid="sbt-new-cheque-mode-manual"/>
                Yeni çek bilgisi manuel gir
              </label>
            </div>
            {form.cheque_mode === "existing" ? (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Portföydeki Uygun Çek</label>
                <select
                  value={form.linked_customer_cheque_id}
                  onChange={e => setForm({ ...form, linked_customer_cheque_id: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                  data-testid="sbt-new-cheque-select"
                >
                  <option value="">-- Çek seçin ({portfolio.length} uygun) --</option>
                  {portfolio.map(ch => (
                    <option key={ch.id} value={ch.id}>
                      {ch.customer_name} · Çek No {ch.check_number || "—"} · {ch.bank_name || "—"} · Vade {ch.check_due_date?.slice(0,10) || "—"} · {money(ch.amount)}
                    </option>
                  ))}
                </select>
                <div className="text-[10px] text-slate-500 italic mt-1">
                  Ciro edildiği anda merkezi çek yaşam döngüsü otomatik güncellenir; Vade Maliyeti gelecek finansman durur (aynı cheque_id).
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input label="Çek No *" value={form.check_number}
                       onChange={e => setForm({ ...form, check_number: e.target.value })}
                       data-testid="sbt-new-check-no"/>
                <Input label="Banka *" value={form.bank_name}
                       onChange={e => setForm({ ...form, bank_name: e.target.value })}
                       data-testid="sbt-new-bank"/>
                <Input label="Vade *" type="date" value={form.check_due_date}
                       onChange={e => setForm({ ...form, check_due_date: e.target.value })}
                       data-testid="sbt-new-check-due"/>
              </div>
            )}
          </div>
        )}

        <Input label="Not" value={form.note}
               onChange={e => setForm({ ...form, note: e.target.value })}
               data-testid="sbt-new-note"/>

        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="sbt-new-err">{err}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <Btn variant="ghost" onClick={onClose}>İptal</Btn>
          <Btn onClick={submit} disabled={busy} className="bg-indigo-600 text-white hover:bg-indigo-700" data-testid="sbt-new-save">
            {busy ? "Kaydediliyor..." : "Kaydet"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// iter102 Tahsilat Makbuzu — helper: fetch authenticated PDF and open in global preview dialog.
async function openReceiptPdf(receiptId) {
  try {
    const token = localStorage.getItem("erp_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/accounting/receipts/${receiptId}/pdf`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { alert(`Makbuz PDF alınamadı (${r.status})`); return; }
    const blob = await r.blob();
    openPdfPreview(blob, `tahsilat-makbuzu-${receiptId}.pdf`);
  } catch (e) { alert("PDF hatası: " + e.message); }
}

function SupplierTxnModal({ supplier, onClose }) {
  const [data, setData] = useState({ summary: {}, transactions: [] });
  const [payFor, setPayFor] = useState(null);  // {purchase, remaining}
  const [newTxnMode, setNewTxnMode] = useState(null); // iter119: 'PURCHASE' | 'PAYMENT'
  const [editTxn, setEditTxn] = useState(null); // iter120
  const [deleteTxn, setDeleteTxn] = useState(null); // iter120
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/accounting/supplier-transactions/${supplier.supplier_id}`);
      setData(r);
    } catch (e) { console.error(e); }
  }, [supplier.supplier_id]);
  useEffect(() => { load(); }, [load]);

  // iter104: compute per-PURCHASE remaining = amount - sum(payments where allocated_to_liability_id == p.id)
  const remainingByPurchase = useMemo(() => {
    const map = {};
    const txns = data.transactions || [];
    for (const t of txns) {
      if (t.transaction_type === "PURCHASE") map[t.id] = Number(t.amount || 0);
    }
    for (const t of txns) {
      if (t.transaction_type === "PAYMENT" && t.allocated_to_liability_id && map[t.allocated_to_liability_id] != null) {
        map[t.allocated_to_liability_id] -= Number(t.amount || 0);
      }
    }
    return map;
  }, [data.transactions]);

  return (
    <Modal open={true} onClose={onClose} title={`Tedarikçi Hareketleri — ${supplier.supplier_name}`} size="full">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <StatCard label="Toplam Alım" value={money(data.summary?.total_purchases)} color="indigo" icon={TrendingUp}/>
        <StatCard label="Toplam Ödeme" value={money(data.summary?.total_payments)} color="emerald" icon={Wallet}/>
        <StatCard label="Kalan Borç" value={money(data.summary?.balance)} color={data.summary?.balance > 0 ? "rose" : "emerald"} icon={DollarSign}/>
      </div>
      {/* iter119: modal-içi hızlı ekleme butonları — tedarikçi otomatik seçili */}
      <div className="flex items-center gap-2 mb-2" data-testid="sbt-modal-quick-actions">
        <Btn
          onClick={() => setNewTxnMode("PURCHASE")}
          className="bg-gradient-to-r from-rose-500 to-red-500 text-white font-bold px-3 py-1.5 shadow"
          data-testid="sbt-modal-new-purchase"
        >
          <PlusCircle size={14}/> Yeni Alım / Borç
        </Btn>
        <Btn
          onClick={() => setNewTxnMode("PAYMENT")}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold px-3 py-1.5 shadow"
          data-testid="sbt-modal-new-payment"
        >
          <Wallet size={14}/> Yeni Ödeme
        </Btn>
        <span className="text-[11px] text-slate-500 italic ml-1">Tedarikçi bilgileri otomatik doldurulur.</span>
      </div>
      <div className="text-xs text-slate-500 italic mb-2">Manuel Alım/Borç kayıtları buradan girilir. Satın Alma modülü artık otomatik tedarikçi bakiyesi <b>oluşturmaz</b>. Her ALIM satırında "Ödeme Ekle" ile ödeme allocate edilebilir.</div>
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Tarih</th>
              <th className="text-left px-3 py-2">Tür</th>
              <th className="text-right px-3 py-2">Tutar</th>
              <th className="text-right px-3 py-2">Kalan</th>
              <th className="text-left px-3 py-2">Vade</th>
              <th className="text-left px-3 py-2">Ödeme</th>
              <th className="text-left px-3 py-2">Not</th>
              <th className="text-left px-3 py-2">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {(data.transactions || []).length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-400">Kayıt yok</td></tr>}
            {(data.transactions || []).map(t => {
              const rem = remainingByPurchase[t.id];
              const isPurchase = t.transaction_type === "PURCHASE";
              return (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs font-mono">{(t.transaction_date || "").slice(0, 10)}</td>
                  <td className="px-3 py-2"><Badge level={isPurchase ? "attention" : "ok"}>{isPurchase ? "Alım" : "Ödeme"}</Badge></td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{money(t.amount)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{isPurchase ? money(rem) : "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono text-amber-700">{t.due_date || "—"}</td>
                  <td className="px-3 py-2 text-xs">{PM_LABEL[t.payment_method] || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[240px]" title={t.note}>{t.note || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 items-center">
                      {isPurchase && rem > 0.005 && (
                        <button
                          onClick={() => setPayFor({ purchase: t, remaining: rem })}
                          className="text-[10px] px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-semibold flex items-center gap-1 border border-emerald-300"
                          data-testid={`sbt-add-payment-${t.id}`}
                        >
                          <Wallet size={10}/> Ödeme Ekle
                        </button>
                      )}
                      {isPurchase && rem !== undefined && rem <= 0.005 && <span className="text-[10px] text-emerald-700 font-bold">✓ Ödendi</span>}
                      {/* iter120 — Sistem Yetkilisi Düzenle / Sil */}
                      <button
                        onClick={() => setEditTxn(t)}
                        className="text-[10px] px-1.5 py-1 rounded bg-slate-100 hover:bg-indigo-100 text-indigo-700 font-semibold border border-slate-300"
                        title="Düzenle"
                        data-testid={`sbt-edit-${t.id}`}
                      ><Pencil size={10}/></button>
                      <button
                        onClick={() => setDeleteTxn(t)}
                        className="text-[10px] px-1.5 py-1 rounded bg-slate-100 hover:bg-rose-100 text-rose-700 font-semibold border border-slate-300"
                        title="Sil (Sistem Yetkilisi)"
                        data-testid={`sbt-delete-${t.id}`}
                      ><Trash2 size={10}/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {payFor && (
        <AddSupplierPaymentModal
          supplier={supplier}
          purchase={payFor.purchase}
          remaining={payFor.remaining}
          onClose={() => setPayFor(null)}
          onSaved={() => { setPayFor(null); load(); }}
        />
      )}
      {newTxnMode && (
        <NewSupplierPurchaseModal
          presetSupplier={supplier}
          presetType={newTxnMode}
          outstandingPurchases={
            newTxnMode === "PAYMENT"
              ? (data.transactions || [])
                  .filter(t => t.transaction_type === "PURCHASE" && (remainingByPurchase[t.id] || 0) > 0.005)
                  .map(t => ({ ...t, remaining: remainingByPurchase[t.id] }))
              : []
          }
          onClose={() => setNewTxnMode(null)}
          onSaved={() => { setNewTxnMode(null); load(); }}
        />
      )}
      {editTxn && (
        <EditSupplierTxnModal
          supplier={supplier}
          txn={editTxn}
          onClose={() => setEditTxn(null)}
          onSaved={() => { setEditTxn(null); load(); }}
        />
      )}
      {deleteTxn && (
        <DeleteSupplierTxnModal
          supplier={supplier}
          txn={deleteTxn}
          onClose={() => setDeleteTxn(null)}
          onDeleted={() => { setDeleteTxn(null); load(); }}
        />
      )}
    </Modal>
  );
}

/* iter120 — Supplier transaction Düzenle modalı.
 * Backend `PUT /api/accounting/supplier-transactions/{tid}` audit trail'e
 * before/after tam snapshot yazar; movement/expense linkage korunur. */
function EditSupplierTxnModal({ supplier, txn, onClose, onSaved }) {
  const [form, setForm] = useState({
    transaction_date: (txn.transaction_date || "").slice(0, 10),
    amount: String(txn.amount ?? ""),
    payment_method: txn.payment_method || "CASH",
    due_date: (txn.due_date || "").slice(0, 10),
    note: txn.note || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isPurchase = txn.transaction_type === "PURCHASE";
  const save = async () => {
    setErr("");
    if (!form.amount || Number(form.amount) <= 0) { setErr("Tutar 0'dan büyük olmalı"); return; }
    if (!form.transaction_date) { setErr("Tarih zorunlu"); return; }
    const payload = {
      supplier_id: supplier.supplier_id,
      transaction_type: txn.transaction_type,
      transaction_date: form.transaction_date,
      amount: Number(form.amount),
      payment_method: form.payment_method,
      due_date: isPurchase ? (form.due_date || null) : null,
      note: form.note || null,
      // preserve linked cheque relations (if any) so PUT validation passes
      linked_customer_cheque_id: txn.linked_customer_cheque_id || null,
      allocated_to_liability_id: txn.allocated_to_liability_id || null,
      check_number: txn.check_number || null,
      bank_name: txn.bank_name || null,
      check_due_date: txn.check_due_date || null,
    };
    setBusy(true);
    try {
      await api.put(`/accounting/supplier-transactions/${txn.id}`, payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Güncellenemedi");
    } finally { setBusy(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title={`Hareket Düzenle — ${isPurchase ? "Alım / Borç" : "Ödeme"}`} size="lg">
      <div className="space-y-3">
        <div className="bg-amber-50 border border-amber-300 rounded p-2 text-[11px] text-amber-900">
          Tedarikçi: <b>{supplier.supplier_name}</b> · İşlem ID: <span className="font-mono">{txn.id?.slice(0, 8)}…</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Input label="Tarih *" type="date" value={form.transaction_date}
                 onChange={e => setForm({ ...form, transaction_date: e.target.value })}
                 data-testid="sbt-edit-date"/>
          <Input label="Tutar (TRY) *" type="number" step="0.01" value={form.amount}
                 onChange={e => setForm({ ...form, amount: e.target.value })}
                 data-testid="sbt-edit-amount"/>
          <Input label="Vade Tarihi" type="date" value={form.due_date}
                 disabled={!isPurchase}
                 onChange={e => setForm({ ...form, due_date: e.target.value })}
                 data-testid="sbt-edit-due"/>
          <Select label="Ödeme Şekli *" value={form.payment_method}
                  onChange={e => setForm({ ...form, payment_method: e.target.value })}
                  data-testid="sbt-edit-pm">
            <option value="CASH">Nakit</option>
            <option value="CREDIT_CARD">Kredi Kartı</option>
            <option value="MAIL_ORDER">Kredi Kartı - Mail Order</option>
            <option value="CHECK">Çek</option>
          </Select>
        </div>
        <Input label="Not" value={form.note}
               onChange={e => setForm({ ...form, note: e.target.value })}
               data-testid="sbt-edit-note"/>
        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="sbt-edit-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <Btn variant="ghost" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={save} disabled={busy} className="bg-indigo-600 text-white hover:bg-indigo-700" data-testid="sbt-edit-save">
            {busy ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

/* iter120 — Supplier transaction Sil (soft-delete) modalı. Sadece admin. */
function DeleteSupplierTxnModal({ supplier, txn, onClose, onDeleted }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isPurchase = txn.transaction_type === "PURCHASE";
  const doDelete = async () => {
    if (!reason.trim()) { setErr("Silme gerekçesi zorunlu"); return; }
    setBusy(true); setErr("");
    try {
      await api.del(`/accounting/supplier-transactions/${txn.id}`, { reason: reason.trim() });
      onDeleted();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Silinemedi");
    } finally { setBusy(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title={`Hareket Sil (Sistem Yetkilisi) — ${isPurchase ? "Alım" : "Ödeme"}`} size="md">
      <div className="space-y-3">
        <div className="bg-rose-50 border border-rose-300 rounded p-2 text-xs text-slate-800">
          <div className="font-semibold text-rose-800 mb-1">Bu hareket silinecek:</div>
          <div><b>Tedarikçi:</b> {supplier.supplier_name}</div>
          <div><b>Tarih:</b> {(txn.transaction_date || "").slice(0, 10)} · <b>Tür:</b> {isPurchase ? "Alım / Borç" : "Ödeme"}</div>
          <div><b>Tutar:</b> <span className="font-mono">{money(txn.amount)}</span></div>
          {txn.transaction_type === "PAYMENT" && (
            <div className="text-amber-800 mt-1">Bu ödemeye bağlı otomatik gider kaydı da otomatik olarak iptal edilecek (soft-delete).</div>
          )}
        </div>
        <Textarea
          label="Silme Gerekçesi (zorunlu — audit trail'e kaydedilir) *"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Örn: Yanlış tutar girildi, iptal edildi..."
          rows={3}
          data-testid="sbt-del-reason"
        />
        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="sbt-del-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <Btn variant="ghost" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={doDelete} disabled={busy || !reason.trim()} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="sbt-del-confirm">
            <Trash2 size={14}/> {busy ? "Siliniyor..." : "Sil"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

/* iter127 — Borç/Alacak "Ödeme Ekle" modalı.
 * Routes through /accounting/debts-credits/pay which dispatches to:
 *   supplier_purchase  → supplier_transactions PAYMENT
 *   customer_balance   → customer_transactions PAYMENT
 *   manual             → income_expenses + dc.paid_amount++
 */
function AddDCPaymentModal({ row, onClose, onSaved }) {
  const remaining = Number(row.remaining_amount ?? row.amount ?? 0);
  const gross = Number(row.gross_amount ?? row.amount ?? 0);
  const paidBefore = Number(row.paid_amount ?? 0);
  const isDebt = row.kind === "debt";
  const isAutoSupplier = row.source === "supplier_purchase";
  const isAutoCustomer = row.source === "customer_balance";
  const isCompanyExpense = row.source === "company_expense";
  const isManual = row.source === "manual";
  const ceSupplierLinked = isCompanyExpense && !!row.supplier_id;
  // Payment method options depend on target ledger (existing schema constraints)
  const pmOptions = (isAutoSupplier || ceSupplierLinked)
    ? [["CASH","Nakit"],["CREDIT_CARD","Kredi Kartı"],["MAIL_ORDER","Kredi Kartı - Mail Order"],["CHECK","Çek"]]
    : (isManual || isCompanyExpense)
    ? [["CASH","Nakit"],["CREDIT_CARD","Kredi Kartı"],["BANK","Banka / Havale"],["CHECK","Çek"],["OTHER","Diğer"]]
    : [["CASH","Nakit"],["CREDIT_CARD","Kredi Kartı"],["MAIL_ORDER","Kredi Kartı - Mail Order"],["CHECK","Çek"]];
  const [form, setForm] = useState({
    transaction_date: todayTR(),
    amount: remaining.toFixed(2),
    payment_method: "CASH",
    note: "",
    check_number: "",
    bank_name: "",
    check_due_date: "",
    check_company: "",
    cheque_mode: "existing",
    linked_customer_cheque_id: "",
  });
  const [portfolio, setPortfolio] = useState([]);
  const [checkCompanies, setCheckCompanies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        if (isAutoSupplier || ceSupplierLinked) {
          const p = await api.get("/accounting/cheques/portfolio");
          setPortfolio(p?.cheques || []);
        }
        if (isManual || isCompanyExpense) {
          const c = await api.get("/accounting/constants");
          setCheckCompanies(c?.check_companies || []);
        }
      } catch (e) {}
    })();
  }, [isAutoSupplier, isManual, isCompanyExpense, ceSupplierLinked]);

  const submit = async () => {
    setErr("");
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { setErr("Tutar 0'dan büyük olmalı"); return; }
    if (amt - remaining > 0.005) { setErr(`Kalan tutardan (${money(remaining)}) fazla olamaz`); return; }
    let refId;
    if (isAutoSupplier) refId = row.id;
    else if (isAutoCustomer) refId = row.customer_id;
    else if (isCompanyExpense) refId = row.id;  // expense_id
    else refId = row.id;
    const payload = {
      source: row.source,
      kind: row.kind,
      ref_id: refId,
      amount: amt,
      transaction_date: form.transaction_date,
      payment_method: form.payment_method,
      note: form.note || null,
    };
    if (form.payment_method === "CHECK") {
      if ((isAutoSupplier || ceSupplierLinked) && form.cheque_mode === "existing") {
        if (!form.linked_customer_cheque_id) { setErr("Portföyden bir çek seçin"); return; }
        payload.linked_customer_cheque_id = form.linked_customer_cheque_id;
      } else {
        if (!form.check_number || !form.bank_name || !form.check_due_date) {
          setErr("Çek No, Banka ve Vade zorunlu"); return;
        }
        payload.check_number = form.check_number;
        payload.bank_name = form.bank_name;
        payload.check_due_date = form.check_due_date;
        if (isManual || (isCompanyExpense && !ceSupplierLinked)) {
          if (!form.check_company) { setErr("Çek firması zorunlu"); return; }
          payload.check_company = form.check_company;
        }
      }
    }
    setBusy(true);
    try {
      await api.post("/accounting/debts-credits/pay", payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Ödeme kaydedilemedi");
    } finally { setBusy(false); }
  };

  const srcLabel = isAutoSupplier ? "Tedarikçi Borcu (Otomatik)"
    : isAutoCustomer ? "Müşteri Alacağı (Otomatik)"
    : isCompanyExpense ? `Şirket Genel Gideri${ceSupplierLinked ? " · Tedarikçi bağlı" : ""}`
    : isDebt ? "Manuel Borç" : "Manuel Alacak";

  return (
    <Modal open={true} onClose={onClose} title={isDebt ? "BORÇ ÖDEMESİ EKLE" : "ALACAK TAHSİLATI EKLE"} size="lg">
      <div className="space-y-3" data-testid="dc-pay-modal">
        <div className={`border rounded p-2 text-[11px] ${isDebt ? "bg-rose-50/60 border-rose-200" : "bg-emerald-50/60 border-emerald-200"}`}>
          <b>{row.name}</b> · <span className="text-slate-600">{srcLabel}</span>
          {row.note && <span className="ml-2 italic text-slate-500">({row.note})</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Toplam Tutar</div><div className="font-mono font-bold" data-testid="dc-pay-gross">{money(gross)}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Daha Önce Ödenen</div><div className="font-mono" data-testid="dc-pay-paid">{money(paidBefore)}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Kalan</div><div className={`font-mono font-bold ${isDebt ? "text-rose-700" : "text-emerald-700"}`} data-testid="dc-pay-remaining">{money(remaining)}</div></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input label="Ödeme Tarihi *" type="date" value={form.transaction_date} onChange={e => setForm({ ...form, transaction_date: e.target.value })} data-testid="dc-pay-date"/>
          <Input label="Ödeme Tutarı *" type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="dc-pay-amount"/>
          <Select label="Ödeme Şekli *" value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} data-testid="dc-pay-pm">
            {pmOptions.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </div>
        {form.payment_method === "CHECK" && (
          <div className="bg-amber-50/60 border border-amber-300 rounded p-2">
            {(isAutoSupplier || ceSupplierLinked) && (
              <div className="flex gap-2 mb-2 text-xs">
                <label className="flex items-center gap-1"><input type="radio" checked={form.cheque_mode === "existing"} onChange={() => setForm({ ...form, cheque_mode: "existing" })}/> Portföyden</label>
                <label className="flex items-center gap-1"><input type="radio" checked={form.cheque_mode === "manual"} onChange={() => setForm({ ...form, cheque_mode: "manual" })}/> Manuel yeni</label>
              </div>
            )}
            {(isAutoSupplier || ceSupplierLinked) && form.cheque_mode === "existing" ? (
              <select value={form.linked_customer_cheque_id} onChange={e => setForm({ ...form, linked_customer_cheque_id: e.target.value })} className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" data-testid="dc-pay-cheque-select">
                <option value="">-- Çek seçin ({portfolio.length} uygun) --</option>
                {portfolio.map(ch => <option key={ch.id} value={ch.id}>{ch.customer_name} · #{ch.check_number || "—"} · {ch.bank_name} · Vade {ch.check_due_date?.slice(0,10)} · {money(ch.amount)}</option>)}
              </select>
            ) : (
              <div className={`grid ${(isManual || (isCompanyExpense && !ceSupplierLinked)) ? "grid-cols-4" : "grid-cols-3"} gap-2`}>
                <Input label="Çek No" value={form.check_number} onChange={e => setForm({ ...form, check_number: e.target.value })} data-testid="dc-pay-check-no"/>
                <Input label="Banka" value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} data-testid="dc-pay-bank"/>
                <Input label="Çek Vade" type="date" value={form.check_due_date} onChange={e => setForm({ ...form, check_due_date: e.target.value })} data-testid="dc-pay-check-due"/>
                {(isManual || (isCompanyExpense && !ceSupplierLinked)) && (
                  <Select label="Çek Firması" value={form.check_company} onChange={e => setForm({ ...form, check_company: e.target.value })} data-testid="dc-pay-check-company">
                    <option value="">Seçin</option>
                    {checkCompanies.map(cc => <option key={cc} value={cc}>{cc}</option>)}
                  </Select>
                )}
              </div>
            )}
          </div>
        )}
        <Input label="Not" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} data-testid="dc-pay-note"/>
        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="dc-pay-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <Btn variant="ghost" onClick={onClose} data-testid="dc-pay-cancel">İptal</Btn>
          <Btn onClick={submit} disabled={busy} className={`${isDebt ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white`} data-testid="dc-pay-save">
            {busy ? "Kaydediliyor..." : "Ödemeyi Kaydet"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}


/* iter104 spec §23-27: Ödeme Ekle modalı — ilgili PURCHASE hareketine allocate. */
function AddSupplierPaymentModal({ supplier, purchase, remaining, onClose, onSaved }) {
  const [portfolio, setPortfolio] = useState([]);
  const [form, setForm] = useState({
    transaction_date: todayTR(),
    amount: remaining.toFixed(2),
    payment_method: "CASH",
    note: "",
    cheque_mode: "existing",
    linked_customer_cheque_id: "",
    check_number: "",
    bank_name: "",
    check_due_date: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const p = await api.get("/accounting/cheques/portfolio");
        setPortfolio(p?.cheques || []);
      } catch (e) {}
    })();
  }, []);

  const submit = async () => {
    setErr("");
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { setErr("Tutar 0'dan büyük olmalı"); return; }
    if (amt - remaining > 0.005) { setErr(`Kalan borçtan (${money(remaining)}) fazla ödeme yapılamaz`); return; }
    const payload = {
      supplier_id: supplier.supplier_id,
      transaction_type: "PAYMENT",
      transaction_date: form.transaction_date,
      amount: amt,
      payment_method: form.payment_method,
      note: form.note || null,
      allocated_to_liability_id: purchase.id,
    };
    if (form.payment_method === "CHECK") {
      if (form.cheque_mode === "existing") {
        if (!form.linked_customer_cheque_id) { setErr("Portföyden bir çek seçin"); return; }
        payload.linked_customer_cheque_id = form.linked_customer_cheque_id;
      } else {
        if (!form.check_number || !form.bank_name || !form.check_due_date) {
          setErr("Yeni çek için Çek No, Banka ve Vade zorunlu"); return;
        }
        payload.check_number = form.check_number;
        payload.bank_name = form.bank_name;
        payload.check_due_date = form.check_due_date;
      }
    }
    setBusy(true);
    try {
      await api.post("/accounting/supplier-transactions", payload);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Ödeme kaydedilemedi");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title="ÖDEME EKLE" size="lg">
      <div className="space-y-3">
        <div className="bg-indigo-50/60 border border-indigo-200 rounded p-2 text-[11px]">
          Bu ödeme <b>{supplier.supplier_name}</b> tedarikçisinin <b>{money(purchase.amount)}</b> tutarlı alım hareketine bağlanacak.
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Alım Tutarı</div><div className="font-mono font-bold">{money(purchase.amount)}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Daha Önce Ödenen</div><div className="font-mono">{money(Number(purchase.amount) - remaining)}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Kalan Tutar</div><div className="font-mono font-bold text-rose-700" data-testid="asp-remaining">{money(remaining)}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Alım Vade</div><div className="font-mono">{purchase.due_date || "—"}</div></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input label="Ödeme Tarihi *" type="date" value={form.transaction_date} onChange={e => setForm({ ...form, transaction_date: e.target.value })} data-testid="asp-date"/>
          <Input label="Ödenecek Tutar *" type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="asp-amount"/>
          <Select label="Ödeme Şekli *" value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })} data-testid="asp-pm">
            <option value="CASH">Nakit</option>
            <option value="CREDIT_CARD">Kredi Kartı</option>
            <option value="MAIL_ORDER">Kredi Kartı - Mail Order</option>
            <option value="CHECK">Çek</option>
          </Select>
        </div>
        {form.payment_method === "CHECK" && (
          <div className="bg-amber-50/60 border border-amber-300 rounded p-2">
            <div className="flex gap-2 mb-2 text-xs">
              <label className="flex items-center gap-1"><input type="radio" checked={form.cheque_mode === "existing"} onChange={() => setForm({ ...form, cheque_mode: "existing" })}/> Portföyden</label>
              <label className="flex items-center gap-1"><input type="radio" checked={form.cheque_mode === "manual"} onChange={() => setForm({ ...form, cheque_mode: "manual" })}/> Manuel yeni</label>
            </div>
            {form.cheque_mode === "existing" ? (
              <select value={form.linked_customer_cheque_id} onChange={e => setForm({ ...form, linked_customer_cheque_id: e.target.value })} className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" data-testid="asp-cheque-select">
                <option value="">-- Çek seçin ({portfolio.length} uygun) --</option>
                {portfolio.map(ch => <option key={ch.id} value={ch.id}>{ch.customer_name} · #{ch.check_number || "—"} · {ch.bank_name} · Vade {ch.check_due_date?.slice(0,10)} · {money(ch.amount)}</option>)}
              </select>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input label="Çek No" value={form.check_number} onChange={e => setForm({ ...form, check_number: e.target.value })}/>
                <Input label="Banka" value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })}/>
                <Input label="Çek Vade" type="date" value={form.check_due_date} onChange={e => setForm({ ...form, check_due_date: e.target.value })}/>
              </div>
            )}
          </div>
        )}
        <Input label="Not" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}/>
        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="asp-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <Btn variant="ghost" onClick={onClose}>İptal</Btn>
          <Btn onClick={submit} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="asp-save">
            {busy ? "Kaydediliyor..." : "Ödemeyi Kaydet"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

/* ================== 1) BAKİYE TAKİP ================== */
function BalanceTrackingTab() {
  const [data, setData] = useState({ customers: [], summary: {} });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [period, setPeriod] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const [selected, setSelected] = useState(null);
  const load = useCallback(async () => {
    const params = { period };
    if (period === "range") { params.start = range.start; params.end = range.end; }
    const r = await api.get("/accounting/balance-tracking", params);
    setData(r);
  }, [period, range]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    let arr = [...(data.customers || [])];
    if (q.trim()) {
      const qt = q.toLowerCase();
      arr = arr.filter(r => (r.customer_name || "").toLowerCase().includes(qt) || (r.note || "").toLowerCase().includes(qt));
    }
    if (status === "debt") arr = arr.filter(r => r.balance > 0);
    else if (status === "zero") arr = arr.filter(r => Math.abs(r.balance) < 0.01);
    else if (status === "credit") arr = arr.filter(r => r.balance < 0);
    if (dateFrom) arr = arr.filter(r => (r.last_transaction_date || "9999") >= dateFrom);
    if (dateTo) arr = arr.filter(r => (r.last_transaction_date || "0000") <= dateTo);
    arr.sort((a, b) => {
      if (sortBy === "balance_desc") return b.balance - a.balance;
      if (sortBy === "balance_asc") return a.balance - b.balance;
      if (sortBy === "recent") return (b.last_transaction_date || "").localeCompare(a.last_transaction_date || "");
      return (a.customer_name || "").localeCompare(b.customer_name || "", "tr");
    });
    return arr;
  }, [data.customers, q, status, dateFrom, dateTo, sortBy]);

  const filteredSummary = useMemo(() => ({
    total_receivables: rows.filter(r => r.balance > 0).reduce((s, r) => s + r.balance, 0),
    total_sales: rows.reduce((s, r) => s + r.total_sales, 0),
    total_payments: rows.reduce((s, r) => s + r.total_payments, 0),
    debtor_count: rows.filter(r => r.balance > 0).length,
  }), [rows]);

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Toplam Alacak" value={money(filteredSummary.total_receivables)} color="rose" icon={DollarSign} testid="stat-receivables"/>
        <StatCard label="Toplam Satış" value={money(filteredSummary.total_sales)} color="indigo" icon={TrendingUp} testid="stat-sales"/>
        <StatCard label="Toplam Tahsilat" value={money(filteredSummary.total_payments)} color="emerald" icon={Wallet} testid="stat-payments"/>
        <StatCard label="Borçlu Müşteri" value={`${filteredSummary.debtor_count} müşteri`} color="amber" icon={Users} testid="stat-debtors"/>
      </div>
      <div className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 mb-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pl-0.5">Arama</label>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-2.5 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Müşteri / not..." className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded" data-testid="bt-search"/>
          </div>
        </div>
        <Select label="Bakiye Durumu" value={status} onChange={e => setStatus(e.target.value)} data-testid="bt-status">
          <option value="all">Tümü</option>
          <option value="debt">Borcu Olanlar</option>
          <option value="zero">Bakiye Sıfır</option>
          <option value="credit">Fazla Ödeme</option>
        </Select>
        <Input label="Son İşl. ≥" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}/>
        <Input label="Son İşl. ≤" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}/>
        <Select label="Sıralama" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="name">Müşteri Adı</option>
          <option value="balance_desc">En Yüksek Bakiye</option>
          <option value="balance_asc">En Düşük Bakiye</option>
          <option value="recent">Son İşlem Tarihi</option>        </Select>
        <Select label="Dönem" value={period} onChange={e => setPeriod(e.target.value)} data-testid="bt-period">
          <option value="all">Tümü</option><option value="today">Bugün</option><option value="week">Bu Hafta</option>
          <option value="month">Bu Ay</option><option value="year">Bu Yıl</option><option value="range">Tarih Aralığı</option>
        </Select>
        {period === "range" && (<>
          <Input label="Başlangıç" type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })}/>
          <Input label="Bitiş" type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })}/>
        </>)}
        {/* iter262/263 — R20 Müşteri Bakiye PDF trio (filtreye duyarlı URL) */}
        <PdfActions url={`/api/accounting/balance-tracking/pdf?period=${period}${period==="range"&&range.start?`&start=${range.start}`:""}${period==="range"&&range.end?`&end=${range.end}`:""}`} filename="Bakiye_Takip.pdf" label="Bakiye PDF" color="blue" testidBase="muh-r20"/>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="bt-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Müşteri Adı</th>
              <th className="text-left px-3 py-2">Son İşlem Tarihi</th>
              <th className="text-right px-3 py-2">Toplam Satış</th>
              <th className="text-right px-3 py-2">Toplam Tahsilat</th>
              <th className="text-right px-3 py-2">Kalan Bakiye</th>
              <th className="text-left px-3 py-2">Notlar</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Kayıt yok.</td></tr>}
            {rows.map(r => (
              <tr key={r.customer_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(r)} data-testid={`bt-row-${r.customer_id}`}>
                <td className="px-3 py-2 font-semibold text-indigo-700">{r.customer_name}</td>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono">{r.last_transaction_date || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{money(r.total_sales)}</td>
                <td className="px-3 py-2 text-right font-mono">{money(r.total_payments)}</td>
                <td className={`px-3 py-2 text-right font-mono font-semibold ${r.balance > 0 ? "text-rose-700" : r.balance < 0 ? "text-emerald-700" : "text-slate-500"}`} data-testid={`bt-balance-${r.customer_id}`}>{money(r.balance)}</td>
                <td className="px-3 py-2 text-xs text-slate-500 truncate max-w-[180px]" title={r.note}>{r.note || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && <CustomerTxnModal customer={selected} onClose={() => { setSelected(null); load(); }}/>}
    </div>
  );
}

/* --- Müşteri Hareketleri Popup --- */
function CustomerTxnModal({ customer, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ summary: {}, transactions: [] });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState({ from: "", to: "", type: "", pm: "", q: "" });
  const emptyForm = { transaction_type: "SALE", transaction_date: todayStr(), amount: "", payment_method: "", check_number: "", bank_name: "", check_due_date: "", check_company: "Meta Pharma Kozmetik", note: "" };
  const [f, setF] = useState(emptyForm);
  // iter102 Tahsilat Makbuzu — per-payment receipt cache (payment_id → {active, cancelled_history})
  const [receipts, setReceipts] = useState({});
  const [receiptModal, setReceiptModal] = useState(null); // { payment } for create modal

  const load = useCallback(async () => {
    const r = await api.get(`/accounting/customer-transactions/${customer.customer_id}`);
    setData(r);
    // Fetch receipt state for all payments in parallel
    const payments = (r.transactions || []).filter(t => t.transaction_type === "PAYMENT" && !t.is_deleted);
    const results = await Promise.all(payments.map(p =>
      api.get(`/accounting/receipts/by-payment/${p.id}`).catch(() => ({ active: null, cancelled_history: [] }))
    ));
    const nextReceipts = {};
    payments.forEach((p, i) => { nextReceipts[p.id] = results[i]; });
    setReceipts(nextReceipts);
  }, [customer.customer_id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!f.amount || parseFloat(f.amount) <= 0) { alert("Tutar 0'dan büyük olmalı"); return; }
    if (f.transaction_type === "PAYMENT" && !f.payment_method) { alert("Ödeme şeklini seçin"); return; }
    setBusy(true);
    try {
      const payload = { customer_id: customer.customer_id, ...f, amount: parseFloat(f.amount) };
      if (f.transaction_type === "SALE") {
        payload.payment_method = null; payload.check_number = null; payload.bank_name = null; payload.check_due_date = null; payload.check_company = null;
      } else if (f.payment_method !== "CHECK") {
        payload.check_number = null; payload.bank_name = null; payload.check_due_date = null; payload.check_company = null;
      }
      if (editing) await api.put(`/accounting/customer-transactions/${editing.id}`, payload);
      else await api.post("/accounting/customer-transactions", payload);
      setF(emptyForm); setEditing(null); await load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Bu hareket iptal edilsin mi? (Soft delete — bakiye yeniden hesaplanır)")) return;
    try { await api.del(`/accounting/customer-transactions/${id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
  };
  const startEdit = (t) => {
    setEditing(t);
    setF({
      transaction_type: t.transaction_type,
      transaction_date: (t.transaction_date || "").slice(0, 10),
      amount: t.amount,
      payment_method: t.payment_method || "",
      check_number: t.check_number || "", bank_name: t.bank_name || "",
      check_due_date: (t.check_due_date || "").slice(0, 10),
      check_company: t.check_company || "Meta Pharma Kozmetik",
      note: t.note || "",
    });
  };

  const filtered = useMemo(() => {
    return (data.transactions || []).filter(t => {
      if (filter.from && (t.transaction_date || "0000") < filter.from) return false;
      if (filter.to && (t.transaction_date || "9999") > filter.to) return false;
      if (filter.type && t.transaction_type !== filter.type) return false;
      if (filter.pm && (t.payment_method || "") !== filter.pm) return false;
      if (filter.q && !((t.note || "").toLowerCase().includes(filter.q.toLowerCase()))) return false;
      return true;
    });
  }, [data.transactions, filter]);

  return (
    <Modal open={true} onClose={onClose} title={`Müşteri Hareketleri — ${customer.customer_name}`} size="full">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <StatCard label="Toplam Satış" value={money(data.summary?.total_sales)} color="indigo" icon={TrendingUp}/>
        <StatCard label="Toplam Ödeme" value={money(data.summary?.total_payments)} color="emerald" icon={Wallet}/>
        <StatCard label="Kalan Bakiye" value={money(data.summary?.balance)} color={data.summary?.balance > 0 ? "rose" : "emerald"} icon={DollarSign}/>
      </div>

      <div className="flex justify-end mb-2">
        <Btn size="sm" variant="secondary" onClick={async () => {
          try {
            const token = localStorage.getItem("erp_token");
            const params = new URLSearchParams({ customer_id: customer.customer_id });
            if (filter.from) params.append("date_from", filter.from);
            if (filter.to) params.append("date_to", filter.to);
            if (filter.type) params.append("transaction_type", filter.type);
            if (filter.pm) params.append("payment_method", filter.pm);
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/accounting/customer-statement-pdf?${params.toString()}`;
            const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) { alert(`PDF alınamadı (${r.status})`); return; }
            const blob = await r.blob();
            openPdfPreview(blob, `hesap-ozeti-${customer.customer_name}-${new Date().toISOString().slice(0,10)}.pdf`);
          } catch (e) { alert("PDF hatası: " + e.message); }
        }} data-testid="ct-download-pdf" title="Filtreli Hesap Özeti PDF"><FileText size={12}/></Btn>
      </div>

      <div className="bg-emerald-50/40 border border-emerald-200 rounded-lg p-3 mb-3">
        <div className="text-xs font-semibold uppercase text-emerald-800 mb-2">{editing ? "Hareketi Düzenle" : "Yeni Hareket Ekle"}</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
          <Select label="İşlem Türü *" value={f.transaction_type} onChange={e => setF({ ...f, transaction_type: e.target.value })} data-testid="ct-type">
            <option value="SALE">Satış</option><option value="PAYMENT">Ödeme</option>
          </Select>
          <Input label="Tarih *" type="date" value={f.transaction_date} onChange={e => setF({ ...f, transaction_date: e.target.value })} data-testid="ct-date"/>
          <Input label="Tutar (TL) *" type="number" step="0.01" min="0" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} data-testid="ct-amount"/>
          {f.transaction_type === "PAYMENT" && (
            <Select label="Ödeme Şekli *" value={f.payment_method} onChange={e => setF({ ...f, payment_method: e.target.value })} data-testid="ct-pm">
              <option value="">Seçiniz...</option>
              <option value="CASH">Nakit</option>
              <option value="CREDIT_CARD">Kredi Kartı</option>
              <option value="CHECK">Çek</option>
            </Select>
          )}
          {f.transaction_type === "PAYMENT" && f.payment_method === "CHECK" && (
            <>
              <Input label="Çek No *" value={f.check_number} onChange={e => setF({ ...f, check_number: e.target.value })} data-testid="ct-check-no"/>
              <Input label="Banka *" value={f.bank_name} onChange={e => setF({ ...f, bank_name: e.target.value })} data-testid="ct-check-bank"/>
              <Input label="Vade *" type="date" value={f.check_due_date} onChange={e => setF({ ...f, check_due_date: e.target.value })} data-testid="ct-check-due"/>
              <Select label="Çek Firması *" value={f.check_company} onChange={e => setF({ ...f, check_company: e.target.value })} data-testid="ct-check-company">
                <option value="Meta Pharma Kozmetik">Meta Pharma Kozmetik</option>
                <option value="Pinus Kozmetik">Pinus Kozmetik</option>
                <option value="Lavion Kozmetik">Lavion Kozmetik</option>
              </Select>
            </>
          )}
          <div className="col-span-6">
            <Input label="Not" value={f.note} onChange={e => setF({ ...f, note: e.target.value })} placeholder="12546 no'lu fatura / Kısmi ödeme / vs." data-testid="ct-note"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          {editing && <Btn size="sm" variant="secondary" onClick={() => { setEditing(null); setF(emptyForm); }}>Vazgeç</Btn>}
          <Btn size="sm" onClick={save} disabled={busy} data-testid="ct-save"><Plus size={12}/> {editing ? "Güncelle" : "Kaydet"}</Btn>
        </div>
      </div>

      <div className="bg-slate-50/60 border border-slate-200 rounded p-2 mb-2 flex flex-wrap gap-2 items-end text-xs">
        <Input label="Başlangıç" type="date" value={filter.from} onChange={e => setFilter({ ...filter, from: e.target.value })}/>
        <Input label="Bitiş" type="date" value={filter.to} onChange={e => setFilter({ ...filter, to: e.target.value })}/>
        <Select label="İşlem Türü" value={filter.type} onChange={e => setFilter({ ...filter, type: e.target.value })}>
          <option value="">Tümü</option><option value="SALE">Satış</option><option value="PAYMENT">Ödeme</option>
        </Select>
        <Select label="Ödeme Şekli" value={filter.pm} onChange={e => setFilter({ ...filter, pm: e.target.value })}>
          <option value="">Tümü</option><option value="CASH">Nakit</option><option value="CREDIT_CARD">Kredi Kartı</option><option value="CHECK">Çek</option>
        </Select>
        <Input label="Notta ara" value={filter.q} onChange={e => setFilter({ ...filter, q: e.target.value })}/>
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-hidden" style={{ maxHeight: 380, overflowY: "auto" }}>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase sticky top-0">
            <tr>
              <th className="text-left px-2 py-2">Tarih</th>
              <th className="text-left px-2 py-2">Tür</th>
              <th className="text-right px-2 py-2">Tutar</th>
              <th className="text-left px-2 py-2">Öd. Şekli</th>
              <th className="text-left px-2 py-2">Çek Bilgisi</th>
              <th className="text-left px-2 py-2">Not</th>
              <th className="text-left px-2 py-2">Giren</th>
              <th className="text-left px-2 py-2">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-400">Hareket yok.</td></tr>}
            {filtered.map(t => (
              <tr key={t.id} className={`border-t border-slate-100 ${t.is_deleted ? "opacity-40 line-through" : ""}`}>
                <td className="px-2 py-1.5 font-mono text-xs">{(t.transaction_date || "").slice(0, 10)}</td>
                <td className="px-2 py-1.5"><Badge level={t.transaction_type === "SALE" ? "attention" : "ok"}>{t.transaction_type === "SALE" ? "Satış" : "Ödeme"}</Badge></td>
                <td className="px-2 py-1.5 text-right font-mono font-semibold">{money(t.amount)}</td>
                <td className="px-2 py-1.5 text-xs">{PM_LABEL[t.payment_method] || "—"}</td>
                <td className="px-2 py-1.5 text-[11px] text-slate-600">
                  {t.payment_method === "CHECK" ? (
                    <span title={`Firma: ${t.check_company}`}>{t.bank_name} #{t.check_number}<br/><span className="text-slate-400">Vade: {(t.check_due_date || "").slice(0, 10)}</span></span>
                  ) : "—"}
                </td>
                <td className="px-2 py-1.5 text-xs truncate max-w-[220px]" title={t.note}>{t.note || "—"}</td>
                <td className="px-2 py-1.5 text-xs">{t.created_by_name || "—"}<br/><span className="text-[10px] text-slate-400">{(t.created_at || "").slice(0, 16).replace("T", " ")}</span></td>
                <td className="px-2 py-1.5">
                  <div className="flex gap-1 flex-wrap">
                    <Btn size="sm" variant="secondary" onClick={() => startEdit(t)} data-testid={`edit-ct-${t.id}`}><Edit2 size={11}/></Btn>
                    <Btn size="sm" variant="danger" onClick={() => del(t.id)} data-testid={`delete-ct-${t.id}`}><Trash2 size={11}/></Btn>
                    {t.transaction_type === "PAYMENT" && !t.is_deleted && (
                      (() => {
                        const rc = receipts[t.id];
                        const active = rc?.active;
                        if (active) {
                          return (
                            <button
                              onClick={() => openReceiptPdf(active.id)}
                              className="text-[10px] px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-semibold flex items-center gap-1 border border-emerald-300"
                              data-testid={`receipt-view-${t.id}`}
                              title={`${active.company_legal_name} — Makbuz #${active.receipt_number}`}
                            >
                              📄 Makbuz #{active.receipt_number}
                            </button>
                          );
                        }
                        return (
                          <button
                            onClick={() => setReceiptModal({ payment: t })}
                            className="text-[10px] px-2 py-1 rounded bg-indigo-50 hover:bg-indigo-100 text-indigo-800 font-semibold flex items-center gap-1 border border-indigo-200"
                            data-testid={`receipt-create-${t.id}`}
                            title="Tahsilat Makbuzu Oluştur"
                          >
                            🧾 Tahsilat Makbuzu Oluştur
                          </button>
                        );
                      })()
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {receiptModal && (
        <ReceiptCreateModal
          payment={receiptModal.payment}
          customer={customer}
          onClose={() => setReceiptModal(null)}
          onSaved={async () => { setReceiptModal(null); await load(); }}
        />
      )}
    </Modal>
  );
}

/* ================== iter102 TAHSİLAT MAKBUZU — Company selection modal ================== */
function ReceiptCreateModal({ payment, customer, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cancelledHistory, setCancelledHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [cs, byPay] = await Promise.all([
          api.get("/accounting/receipts/companies"),
          api.get(`/accounting/receipts/by-payment/${payment.id}`),
        ]);
        setCompanies(cs || []);
        setCancelledHistory(byPay?.cancelled_history || []);
      } catch (e) { setErr(e?.message || "Firmalar yüklenemedi"); }
    })();
  }, [payment.id]);

  const create = async () => {
    if (!companyId) { setErr("Ödeme yapılan firmayı seçin"); return; }
    setBusy(true); setErr("");
    try {
      const r = await api.post("/accounting/receipts", {
        payment_id: payment.id, company_id: companyId,
      });
      // Immediately open PDF preview
      await openReceiptPdf(r.id);
      await onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Makbuz oluşturulamadı");
    } finally { setBusy(false); }
  };

  const cancelReceipt = async (rid) => {
    const reason = window.prompt("İptal sebebi (zorunlu):");
    if (!reason || !reason.trim()) return;
    if (!window.confirm("Bu makbuz İPTAL edilecek. Numara TEKRAR KULLANILAMAZ. Devam?")) return;
    try {
      await api.post(`/accounting/receipts/${rid}/cancel`, { reason: reason.trim() });
      await onSaved();
    } catch (e) { alert(e?.response?.data?.detail || "İptal başarısız"); }
  };

  const pm = (payment.payment_method || "").toUpperCase();
  const pmDisp = { CASH: "Nakit", CREDIT_CARD: "Kredi Kartı", MAIL_ORDER: "Kredi Kartı - Mail Order", CHECK: "Çek" }[pm] || pm || "—";

  return (
    <Modal open={true} onClose={onClose} title="TAHSİLAT MAKBUZU OLUŞTUR" size="lg">
      <div className="space-y-3">
        <div className="bg-indigo-50/60 border border-indigo-200 rounded p-3 text-xs text-indigo-900" data-testid="receipt-modal-info">
          Bu makbuz mevcut <b>ÖDEME</b> hareketinin resmi belgesidir — yeni bir ödeme
          oluşturmaz. Firma seçildikten sonra makbuz numarası <b>o firmaya özel serideki
          sıradaki numara</b> olarak atanır ve sonradan değiştirilemez.
        </div>

        {/* Company select */}
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Ödeme Yapılan Firma *</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="receipt-company-list">
            {companies.map(c => (
              <button
                key={c.company_id}
                onClick={() => setCompanyId(c.company_id)}
                className={`text-left p-3 rounded border-2 transition ${companyId === c.company_id ? "border-indigo-600 bg-indigo-50" : "border-slate-200 hover:border-indigo-300 bg-white"}`}
                data-testid={`receipt-company-${c.company_id}`}
              >
                <div className="text-xs font-bold text-slate-900">{c.receipt_series}</div>
                <div className="text-[10px] text-slate-600 leading-tight mt-1 line-clamp-3">{c.legal_name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Payment readonly summary */}
        <div className="bg-slate-50 border border-slate-200 rounded p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div>
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Müşteri</div>
            <div className="font-semibold text-slate-900">{customer.customer_name}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Ödeme Tarihi</div>
            <div className="font-mono">{(payment.transaction_date || "").slice(0, 10)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Tutar</div>
            <div className="font-mono font-bold text-emerald-800">{money(payment.amount)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500 font-semibold">Ödeme Şekli</div>
            <div className="font-semibold">{pmDisp}</div>
          </div>
          {pm === "CHECK" && (
            <div className="col-span-2 md:col-span-4 border-t border-slate-200 pt-2 mt-1">
              <div className="text-[10px] uppercase text-slate-500 font-semibold mb-0.5">Çek Bilgileri</div>
              <div className="text-[11px] text-slate-700">
                <b>Banka:</b> {payment.bank_name || "—"} · <b>Çek No:</b> {payment.check_number || "—"} · <b>Vade:</b> {(payment.check_due_date || "").slice(0, 10) || "—"}
              </div>
            </div>
          )}
        </div>

        {cancelledHistory.length > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded p-2 text-[11px]" data-testid="receipt-cancelled-history">
            <div className="font-bold text-amber-900 mb-1">Bu ödeme için önceden iptal edilmiş makbuzlar (numara tekrar kullanılmaz):</div>
            <ul className="list-disc pl-4 text-amber-900 space-y-0.5">
              {cancelledHistory.map(c => (
                <li key={c.id}>
                  <b>{c.company_id}</b> #{c.receipt_number} — {c.cancelled_by_name} · {(c.cancellation_reason || "sebep girilmedi").slice(0, 80)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="receipt-err">{err}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <Btn variant="ghost" onClick={onClose}>İPTAL</Btn>
          <Btn onClick={create} disabled={busy || !companyId} className="bg-indigo-600 text-white hover:bg-indigo-700" data-testid="receipt-submit">
            {busy ? "Oluşturuluyor..." : "MAKBUZU OLUŞTUR"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

/* ================== 2) MALİ ANALİZ ================== */
function FinancialAnalysisTab() {
  const { user } = useAuth();
  // [msg-145 Bölüm 3+4] Kârlılık kartı + Şirket Giderleri (Gerçek Kâr) yalnızca Sistem Yöneticisi'ne.
  // Envanter Değer Kartı ve dış giderler listesi tüm muhasebe kullanıcılarına açık kalır.
  const isAdmin = user?.role === "admin";
  return (
    <div className="space-y-4">
      {/* iter262 — R16/R17/R18 PDF trio Mali Analiz sayfasının üstünde */}
      {isAdmin && (
        <div className="flex flex-wrap justify-end gap-2 items-center bg-slate-50/60 border border-slate-200 rounded-md p-2">
          <PdfActions url="/api/accounting/profitability/pdf?period=month" filename="Karlilik_Raporu.pdf" label="Karlılık PDF" color="indigo" testidBase="muh-r16"/>
          <PdfActions url="/api/accounting/expenses/pdf" filename="Gider_Raporu.pdf" label="Gider PDF" color="amber" testidBase="muh-r17"/>
          <PdfActions url="/api/accounting/inventory-value/pdf" filename="Envanter_Deger_Raporu.pdf" label="Envanter Değer PDF" color="emerald" testidBase="muh-r18"/>
        </div>
      )}
      {isAdmin && <ProfitabilityCard/>}
      {isAdmin && <CompanyExpensesCard/>}
      <ExpensesListingCard/>
      <InventoryValueCard/>
      {!isAdmin && (
        <Card className="p-4 border-amber-300 bg-amber-50 text-xs text-amber-800" data-testid="muh-profitability-locked">
          🔒 Kârlılık Özeti ve Şirket Giderleri raporları yalnızca <b>Sistem Yöneticisi</b> tarafından görüntülenebilir. Bakiye Takip, Gelir/Gider ve Borç/Alacak sekmelerine erişebilirsiniz.
        </Card>
      )}
    </div>
  );
}

/* ======= Genel Şirket Giderleri (Gerçek Kârlılık'ta kullanılır) ======= */
function CompanyExpensesCard() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [f, setF] = useState({ name: "", amount: "", recurrence: "monthly", note: "" });
  const [editId, setEditId] = useState(null);
  const load = useCallback(async () => {
    const r = await api.get("/accounting/company-expenses");
    setRows(r.rows || []); setSummary(r.summary || {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!f.name.trim() || !parseFloat(f.amount)) { alert("Gider adı ve tutar zorunlu"); return; }
    const payload = { name: f.name.trim(), amount: parseFloat(f.amount), recurrence: f.recurrence, note: f.note || null };
    try {
      if (editId) await api.put(`/accounting/company-expenses/${editId}`, payload);
      else await api.post("/accounting/company-expenses", payload);
      setF({ name: "", amount: "", recurrence: "monthly", note: "" }); setEditId(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const edit = (r) => { setEditId(r.id); setF({ name: r.name, amount: String(r.amount), recurrence: r.recurrence, note: r.note || "" }); };
  const del = async (id) => { if (window.confirm("Bu şirket giderini silmek istiyor musunuz?")) { await api.del(`/accounting/company-expenses/${id}`); load(); } };
  return (
    <Card className="p-4" data-testid="ma-company-expenses">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-bold text-slate-800 flex items-center gap-2"><Wallet size={14}/> Genel Şirket Giderleri</div>
          <div className="text-[11px] text-slate-500">Aylık ve yıllık sabit giderler (kira, maaş, elektrik…). <b>Girişler TL cinsindendir</b>; kayıt anındaki EUR/TRY kuru snapshot alınır ve tüm Mali Analiz hesaplamalarına EUR olarak dahil edilir. Malzeme/ambalaj/formül işçiliği burada tekrar girilmez.</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
        <StatCard label="Aylık Toplam (EUR)" value={moneyEur(summary.monthly_total_eur)} color="rose" icon={TrendingDown} testid="ce-monthly-eur"/>
        <StatCard label="Yıllık Toplam (EUR)" value={moneyEur(summary.yearly_total_eur)} color="rose" icon={TrendingDown} testid="ce-yearly-eur"/>
        <StatCard label="Aylık Eşdeğer (EUR)" value={moneyEur(summary.monthly_equivalent_eur)} color="amber" icon={PieChart} testid="ce-monthly-eq-eur"/>
        <StatCard label="Günlük Gider (EUR / 22 gün)" value={moneyEur(summary.daily_expense_eur)} color="indigo" icon={DollarSign} testid="ce-daily-eur"/>
      </div>
      <div className="text-[11px] text-slate-500 mb-3 bg-slate-50/60 border border-slate-200 rounded px-2 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5" data-testid="ce-try-strip">
        <span>💱 Kur: <b>1 € = {Number(summary?.eur_try_rate ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })} TL</b> (Sistem &gt; FX)</span>
        <span>·</span>
        <span>TL karşılığı → Aylık: <b>{moneyTry(summary.monthly_total_try ?? summary.monthly_total)}</b></span>
        <span>·</span>
        <span>Yıllık: <b>{moneyTry(summary.yearly_total_try ?? summary.yearly_total)}</b></span>
        <span>·</span>
        <span>Aylık Eşdeğer: <b>{moneyTry(summary.monthly_equivalent_try ?? summary.monthly_equivalent)}</b></span>
      </div>
      <div className="flex flex-wrap gap-2 items-end mb-3 bg-slate-50/60 border border-slate-200 rounded p-2 text-xs">
        <Input label="Gider Adı *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Kira, Maaş, Elektrik..." data-testid="ce-name"/>
        <Input label="Tutar (TL) *" type="number" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} data-testid="ce-amount"/>
        <Select label="Tekrar" value={f.recurrence} onChange={e => setF({ ...f, recurrence: e.target.value })} data-testid="ce-recurrence">
          <option value="monthly">Aylık</option>
          <option value="yearly">Yıllık</option>
        </Select>
        <Input label="Not" value={f.note} onChange={e => setF({ ...f, note: e.target.value })}/>
        <Btn onClick={save} data-testid="ce-save"><Plus size={12}/> {editId ? "Güncelle" : "Ekle"}</Btn>
        {editId && <Btn variant="secondary" onClick={() => { setEditId(null); setF({ name:"", amount:"", recurrence:"monthly", note:"" }); }}>Vazgeç</Btn>}
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-hidden" style={{ maxHeight: 300, overflowY: "auto" }}>
        <table className="w-full text-sm" data-testid="ce-table">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase sticky top-0"><tr>
            <th className="text-left px-2 py-2">Adı</th>
            <th className="text-right px-2 py-2">Tutar (TL)</th>
            <th className="text-right px-2 py-2">Karşılığı (EUR)</th>
            <th className="text-left px-2 py-2">Tekrar</th>
            <th className="text-left px-2 py-2">Not</th>
            <th className="text-right px-2 py-2">İşlem</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-400">Henüz şirket gideri eklenmemiş.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`ce-row-${r.id}`}>
                <td className="px-2 py-1.5 font-medium">{r.name}</td>
                <td className="px-2 py-1.5 text-right font-mono">{moneyTry(r.amount)}</td>
                <td className="px-2 py-1.5 text-right font-mono text-indigo-700" title={`Snapshot kur: 1€ = ${Number(r.eur_try_rate_snapshot || 0).toFixed(4)} TL`}>{moneyEur(r.amount_eur)}</td>
                <td className="px-2 py-1.5"><Badge level={r.recurrence === "monthly" ? "attention" : "inactive"}>{r.recurrence === "monthly" ? "Aylık" : "Yıllık"}</Badge></td>
                <td className="px-2 py-1.5 text-xs text-slate-500">{r.note || "—"}</td>
                <td className="px-2 py-1.5 text-right">
                  <button onClick={() => edit(r)} className="text-indigo-600 hover:bg-indigo-50 p-1 rounded mr-1" data-testid={`ce-edit-${r.id}`}><Edit2 size={12}/></button>
                  <button onClick={() => del(r.id)} className="text-rose-600 hover:bg-rose-50 p-1 rounded" data-testid={`ce-delete-${r.id}`}><Trash2 size={12}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ProfitabilityCard() {
  const [mode, setMode] = useState("forward"); // forward | production | real
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState({ start: "", end: "" });
  const [customer, setCustomer] = useState("");
  const [customerText, setCustomerText] = useState("");
  const [orderNo, setOrderNo] = useState("");
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState({ rows: [], summary: {} });
  const load = useCallback(async () => {
    const params = { mode, period };
    if (period === "range") { params.start = range.start; params.end = range.end; }
    if (customer) params.customer_id = customer;
    if (orderNo) params.order_no = orderNo;
    const [r, cs] = await Promise.all([api.get("/accounting/profitability-v2", params), api.get("/customers")]);
    setData(r); setCustomers(cs);
  }, [mode, period, range, customer, orderNo]);
  useEffect(() => { load(); }, [load]);

  const modeMeta = {
    forward: { label: "İleri Vadeli Kârlılık", hint: "Onaylanmış ancak Sevkiyata Hazır olmayan siparişler (canlı maliyet). Onay tarihi ile filtrelenir." },
    production: { label: "Üretim Kârlılığı", hint: "Sevkiyata Hazır ve sonrası siparişler. Snapshot maliyet ile — geçmiş kâr korunur." },
    real: { label: "Gerçek Kârlılık", hint: "Üretim ile aynı sipariş seti, işçilik hariç maliyet + döneme dağıtılan şirket gideri düşülür." },
  };

  return (
    <Card className="p-4" data-testid="ma-profitability">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-bold text-slate-800 flex items-center gap-2"><TrendingUp size={14}/> Kârlılık Özeti</div>
          <div className="text-[11px] text-slate-500">{modeMeta[mode].hint}</div>
        </div>
      </div>
      <div className="flex gap-1 mb-3 border-b border-slate-200">
        {Object.entries(modeMeta).map(([k, v]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`pr-mode-${k}`}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 -mb-px transition ${mode === k ? "border-indigo-600 text-indigo-700 bg-indigo-50/40" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {v.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <StatCard label={mode === "forward" ? "Beklenen Gelir" : "Toplam Gelir"} value={moneyEur(data.summary?.total_revenue)} color="indigo" icon={TrendingUp} testid="pr-revenue"/>
        <StatCard label={mode === "real" ? "Maliyet (İşçilik Hariç)" : mode === "forward" ? "Beklenen Maliyet" : "Toplam Maliyet"} value={moneyEur(data.summary?.total_cost)} color="amber" icon={Package} testid="pr-cost"/>
        {mode === "real" ? (
          <StatCard label={`Şirket Gideri (${data.summary?.working_days || 0} iş günü)`} value={moneyEur(data.summary?.company_expense_period)} color="rose" icon={Wallet} testid="pr-company-expense"/>
        ) : (
          <StatCard label={mode === "forward" ? "Beklenen Kâr" : "Toplam Kâr"} value={moneyEur(data.summary?.total_profit)} color={data.summary?.total_profit >= 0 ? "emerald" : "rose"} icon={DollarSign} testid="pr-profit"/>
        )}
        {mode === "real" ? (
          <StatCard label="Gerçek Kâr" value={moneyEur(data.summary?.real_profit)} color={(data.summary?.real_profit || 0) >= 0 ? "emerald" : "rose"} icon={DollarSign} testid="pr-real-profit"/>
        ) : (
          <StatCard label="Ort. Kâr Marjı" value={`%${(data.summary?.avg_margin_pct || 0).toFixed(1)}`} color="emerald" icon={PieChart} testid="pr-margin"/>
        )}
      </div>
      <div className="flex flex-wrap gap-2 items-end mb-2 bg-slate-50/60 border border-slate-200 rounded p-2 text-xs">
        <Select label="Dönem" value={period} onChange={e => setPeriod(e.target.value)} data-testid="pr-period">
          <option value="today">Bugün</option><option value="week">Bu Hafta</option><option value="month">Bu Ay</option>
          <option value="year">Bu Yıl</option><option value="range">Tarih Aralığı</option>
        </Select>
        {period === "range" && (<>
          <Input label="Başlangıç" type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })}/>
          <Input label="Bitiş" type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })}/>
        </>)}
        <div className="flex-1 min-w-[200px]">
          <SmartSuggestInput
            label="Müşteri (opsiyonel)"
            value={customerText}
            selectedId={customer}
            items={customers}
            displayField="legal_name"
            idField="id"
            secondaryField={(c) => [c.code && `Kod: ${c.code}`, c.city].filter(Boolean).join(" · ")}
            placeholder="Tümü — veya müşteri arayın..."
            onChange={({ value, selectedId }) => { setCustomerText(value || ""); setCustomer(selectedId || ""); }}
            data-testid="pr-customer"
          />
        </div>
        <Input label="Sipariş No" value={orderNo} onChange={e => setOrderNo(e.target.value)} placeholder="SO-..."/>
        <Btn size="sm" onClick={load} data-testid="pr-filter-btn">Filtrele</Btn>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-hidden" style={{ maxHeight: 380, overflowY: "auto" }}>
        <table className="w-full text-sm" data-testid="pr-table">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase sticky top-0">
            <tr>
              <th className="text-left px-2 py-2">Tarih</th>
              <th className="text-left px-2 py-2">Sipariş</th>
              <th className="text-left px-2 py-2">Müşteri</th>
              <th className="text-left px-2 py-2">Ürün</th>
              <th className="text-right px-2 py-2">Adet</th>
              <th className="text-right px-2 py-2">Birim Satış</th>
              <th className="text-right px-2 py-2">Birim Mlyt{mode === "real" ? " (İşç. Hariç)" : ""}</th>
              <th className="text-right px-2 py-2">Toplam Satış</th>
              <th className="text-right px-2 py-2">Toplam Mlyt</th>
              <th className="text-right px-2 py-2">Kâr</th>
              <th className="text-right px-2 py-2">Marj %</th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).length === 0 && <tr><td colSpan={11} className="text-center py-8 text-slate-400">Bu dönem için kayıt yok.</td></tr>}
            {(data.rows || []).map((r, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-2 py-1.5 font-mono text-xs">{r.date}</td>
                <td className="px-2 py-1.5 font-mono text-xs">{r.order_no}{r.cost_snapshot_missing && <span className="ml-1 text-amber-600" title="Bu sipariş için maliyet snapshot bulunamadı — canlı formülden hesaplandı">⚠</span>}</td>
                <td className="px-2 py-1.5 text-xs">{r.customer_name}</td>
                <td className="px-2 py-1.5 text-xs">{r.product_name}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.qty}</td>
                <td className="px-2 py-1.5 text-right font-mono">{moneyEur(r.unit_price)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{moneyEur(r.unit_cost)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{moneyEur(r.revenue)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{moneyEur(r.total_cost)}</td>
                <td className={`px-2 py-1.5 text-right font-mono font-semibold ${r.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{moneyEur(r.profit)}</td>
                <td className={`px-2 py-1.5 text-right font-mono ${r.margin_pct >= 0 ? "text-emerald-700" : "text-red-600"}`}>%{r.margin_pct.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {mode === "real" && (
        <div className="mt-2 text-[11px] bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-800">
          <strong>Gerçek Kâr Hesabı:</strong> Toplam Satış {moneyEur(data.summary?.total_revenue)} − Maliyet (İşçilik Hariç) {moneyEur(data.summary?.total_cost)} − Şirket Gideri {moneyEur(data.summary?.company_expense_period)} = <strong>{moneyEur(data.summary?.real_profit)}</strong> · Marj: %{(data.summary?.real_margin_pct || 0).toFixed(1)} · Günlük gider: {moneyEur(data.summary?.daily_company_expense)}
          {data.summary?.eur_try_rate ? <span className="ml-2 text-emerald-700">· Kur: 1€ = {Number(data.summary.eur_try_rate).toFixed(4)} TL</span> : null}
        </div>
      )}
    </Card>
  );
}

function ExpensesListingCard() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalEur, setTotalEur] = useState(0);
  const [eurRate, setEurRate] = useState(0);
  const [month, setMonth] = useState("");
  const [category, setCategory] = useState("");
  const [constants, setConstants] = useState({ expense_categories: [] });
  const load = useCallback(async () => {
    const [r, c] = await Promise.all([
      api.get("/accounting/expenses-listing", { ...(month && { month }), ...(category && { category }) }),
      api.get("/accounting/constants"),
    ]);
    setRows(r.rows); setTotal(r.total_try ?? r.total); setTotalEur(r.total_eur ?? 0); setEurRate(r.eur_try_rate ?? 0); setConstants(c);
  }, [month, category]);
  useEffect(() => { load(); }, [load]);
  return (
    <>
      {/* iter106: Gider Listesi UI kaldırıldı — merkezi gider listesi
          artık yalnız Muhasebe → Gelir/Gider'de gösterilir. Kar/zarar
          hesaplamalarında aynı expense verisi kullanılmaya devam eder. */}
      <div style={{ display: "none" }} data-testid="ma-expenses-hidden">
        <span data-testid="ma-expenses-total-eur">{moneyEur(totalEur)}</span>
        <span data-testid="ma-expenses-total-try">{moneyTry(total)}</span>
      </div>
      {false && (
      <Card className="p-4" data-testid="ma-expenses">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-bold text-slate-800 flex items-center gap-2"><TrendingDown size={14}/> Gider Listesi</div>
          <div className="text-[11px] text-slate-500">Gelir Gider modülünde girilen manuel giderler. Tek kaynak — burada duplicate yok. Girişler TL, toplamlar EUR olarak normalize edilir.</div>
        </div>
          <div className="text-right">
            <div className="text-sm font-mono font-bold text-rose-700" data-testid="ma-expenses-total-eur">Toplam: {moneyEur(totalEur)}</div>
            <div className="text-[10px] text-slate-500 font-mono" data-testid="ma-expenses-total-try">TL karşılığı: {moneyTry(total)}{eurRate ? <> · 1€={Number(eurRate).toFixed(4)} TL</> : null}</div>
          </div>
      </div>
      <div className="flex gap-2 items-end mb-2 bg-slate-50/60 border border-slate-200 rounded p-2 text-xs">
        <Input label="Ay (YYYY-MM)" type="month" value={month} onChange={e => setMonth(e.target.value)}/>
        <Select label="Gider Türü" value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">Tümü</option>
          {(constants.expense_categories || []).map(c => <option key={c} value={c}>{c}</option>)}
        </Select>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-hidden" style={{ maxHeight: 380, overflowY: "auto" }}>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase sticky top-0">
            <tr>
              <th className="text-left px-2 py-2">Tarih</th>
              <th className="text-left px-2 py-2">Gider Türü</th>
              <th className="text-left px-2 py-2">Açıklama</th>
              <th className="text-right px-2 py-2">Tutar (TL)</th>
              <th className="text-right px-2 py-2">Karşılığı (EUR)</th>
              <th className="text-left px-2 py-2">Ödeme Şekli</th>
              <th className="text-left px-2 py-2">Giren</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-400">Gider kaydı yok. Gelir Gider sekmesinden ekleyin.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-2 py-1.5 font-mono text-xs">{(r.entry_date || "").slice(0, 10)}</td>
                <td className="px-2 py-1.5 text-xs">{r.category || "—"}</td>
                <td className="px-2 py-1.5">{r.description}</td>
                <td className="px-2 py-1.5 text-right font-mono">{moneyTry(r.amount)}</td>
                <td className="px-2 py-1.5 text-right font-mono text-indigo-700">{moneyEur(r.amount_eur)}</td>
                <td className="px-2 py-1.5 text-xs">{PM_LABEL[r.payment_method] || "—"}</td>
                <td className="px-2 py-1.5 text-xs">{r.created_by_name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
      )}
    </>
  );
}

function InventoryValueCard() {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("value_desc");
  const [lastRefresh, setLastRefresh] = useState(null);
  const load = useCallback(async () => {
    const r = await api.get("/accounting/inventory-value", kind ? { kind } : {});
    setData(r);
    setLastRefresh(new Date());
  }, [kind]);
  // iter329.9 — 15 sn canlı polling.  Sekme arka plandayken (visibilitychange hidden)
  // boşa istek atmamak için duraklatılır.  Fetch sırasında kullanıcı sort/filter'ı
  // değiştirirse yeni load zaten tetiklenir.
  useEffect(() => {
    load();
    const tick = () => { if (!document.hidden) load(); };
    const id = setInterval(tick, 15000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);
  const rows = useMemo(() => {
    let arr = [...(data.rows || [])];
    if (q.trim()) arr = arr.filter(r => (r.material_name || "").toLowerCase().includes(q.toLowerCase()) || (r.material_code || "").toLowerCase().includes(q.toLowerCase()));
    arr.sort((a, b) => {
      if (sortBy === "value_asc") return a.total_value - b.total_value;
      if (sortBy === "name") return (a.material_name || "").localeCompare(b.material_name || "", "tr");
      return b.total_value - a.total_value;
    });
    return arr;
  }, [data.rows, q, sortBy]);
  return (
    <Card className="p-4" data-testid="ma-inventory-value">
      <div className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2">
        <Package size={14}/> Envanter Mali Değeri
        <span className="text-[10px] text-slate-500 font-normal">(EUR normalize)</span>
        <span className="ml-auto text-[10px] text-slate-400 flex items-center gap-1" data-testid="iv-last-refresh">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
          Canlı · 15 sn{lastRefresh ? ` · ${lastRefresh.toLocaleTimeString("tr-TR")}` : ""}
        </span>
        <button onClick={load} className="text-[10px] px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 rounded border"
          data-testid="iv-refresh" title="Şimdi yenile">↻</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <StatCard label="Genel Toplam" value={moneyEur(data.summary?.grand_total)} color="indigo" icon={DollarSign} testid="iv-total"/>
        <StatCard label="Hammadde Toplamı" value={moneyEur(data.summary?.raw_total)} color="amber" icon={Package} testid="iv-raw"/>
        <StatCard label="Ambalaj Toplamı" value={moneyEur(data.summary?.packaging_total)} color="emerald" icon={Package} testid="iv-pkg"/>
      </div>
      <div className="flex gap-2 items-end mb-2 bg-slate-50/60 border border-slate-200 rounded p-2 text-xs">
        <div className="flex-1">
          <label className="text-[10px] uppercase text-slate-500 font-semibold pl-0.5">Ara</label>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Malzeme adı / kod..." className="w-full px-2 py-1.5 border border-slate-200 rounded"/>
        </div>
        <Select label="Tür" value={kind} onChange={e => setKind(e.target.value)}>
          <option value="">Tümü</option><option value="raw">Hammadde</option><option value="packaging">Ambalaj</option>
        </Select>
        <Select label="Sıralama" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="value_desc">En Yüksek Değer</option>
          <option value="value_asc">En Düşük Değer</option>
          <option value="name">Malzeme Adı</option>
        </Select>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-hidden" style={{ maxHeight: 380, overflowY: "auto" }}>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase sticky top-0">
            <tr>
              <th className="text-left px-2 py-2">Malzeme</th>
              <th className="text-left px-2 py-2">Tür</th>
              <th className="text-right px-2 py-2">Mevcut Stok</th>
              <th className="text-left px-2 py-2">Birim</th>
              <th className="text-right px-2 py-2">Birim Fiyat (EUR)</th>
              <th className="text-right px-2 py-2">Stok Değeri (EUR)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-400">Stok verisi yok.</td></tr>}
            {rows.map(r => (
              <tr key={r.material_id} className="border-t border-slate-100">
                <td className="px-2 py-1.5">
                  <div className="font-semibold text-slate-800">{r.material_name}</div>
                  {r.material_code && <div className="text-[10px] font-mono text-slate-400">{r.material_code}</div>}
                </td>
                <td className="px-2 py-1.5"><Badge level={r.kind === "raw" ? "attention" : "ok"}>{r.kind_label}</Badge></td>
                <td className="px-2 py-1.5 text-right font-mono">{r.qty}</td>
                <td className="px-2 py-1.5 text-xs">{r.unit}</td>
                <td className="px-2 py-1.5 text-right font-mono" title={r.currency_native && r.currency_native !== "EUR" ? `Kaynak: ${r.unit_cost_native} ${r.currency_native}` : ""}>{moneyEur(r.unit_cost)}</td>
                <td className="px-2 py-1.5 text-right font-mono font-semibold text-indigo-700">{moneyEur(r.total_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ================== 3) GELİR GİDER ================== */
function CashflowTab() {
  const [data, setData] = useState({ rows: [], summary: {}, pagination: { page: 1, total_pages: 1, total_count: 0, has_more: false } });
  const [constants, setConstants] = useState({ expense_categories: [], payment_methods_ie: [] });
  const [period, setPeriod] = useState("year");
  const [range, setRange] = useState({ start: "", end: "" });
  const [type, setType] = useState("");
  const [pm, setPm] = useState("");
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState("INCOME");
  const [editing, setEditing] = useState(null);
  const [chequeDrillOpen, setChequeDrillOpen] = useState(false);

  const load = useCallback(async () => {
    const params = { period, page, page_size: pageSize };
    if (period === "range") { params.start = range.start; params.end = range.end; }
    if (type) params.type = type;
    if (pm) params.payment_method = pm;
    if (source) params.source = source;
    if (q) params.q_text = q;
    const [r, c] = await Promise.all([api.get("/accounting/income-expense", params), api.get("/accounting/constants")]);
    setData(r); setConstants(c);
  }, [period, range, type, pm, source, q, page, pageSize]);
  useEffect(() => { load(); }, [load]);
  // Filtre değişince page=1'e dön (5 yıl öncesine gitmişse, filtre değişimiyle başa sar).
  useEffect(() => { setPage(1); }, [period, range.start, range.end, type, pm, source, q, pageSize]);

  const openAdd = (t) => { setAddType(t); setEditing(null); setAddOpen(true); };
  const openEdit = (r) => { if (r.is_projected) { alert("Bu kayıt Müşteri Ödemesi'nden yansımıştır. Bakiye Takip > müşteri hareketleri üzerinden düzenleyin."); return; } setEditing(r); setAddType(r.type); setAddOpen(true); };
  const del = async (r) => {
    if (r.is_projected) { alert("Bu kayıt Müşteri Ödemesi'nden yansımıştır. Bakiye Takip üzerinden silinmelidir."); return; }
    if (!window.confirm("Bu kayıt iptal edilsin mi? (Soft delete)")) return;
    try { await api.del(`/accounting/income-expense/${r.id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <StatCard label="Toplam Gelir" value={money(data.summary?.total_income)} color="emerald" icon={TrendingUp} testid="cf-income"/>
        <StatCard label="Toplam Gider" value={money(data.summary?.total_expense)} color="rose" icon={TrendingDown} testid="cf-expense"/>
        <StatCard
          label="Net Nakit Hareketi"
          value={money(data.summary?.net_cash)}
          color={(data.summary?.net_cash ?? 0) >= 0 ? "emerald" : "rose"}
          icon={DollarSign}
          testid="cf-net-cash"
        />
        <StatCard
          label="Net Çek Hareketi"
          value={money(data.summary?.net_cheque)}
          color={(data.summary?.net_cheque ?? 0) >= 0 ? "indigo" : "rose"}
          icon={Wallet}
          testid="cf-net-cheque"
          onClick={() => setChequeDrillOpen(true)}
        />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-4 text-[10px] text-slate-500">
        <div className="italic">Çek hariç gerçekleşen net para hareketi (Nakit / Havale / Kredi Kartı / Mail Order).</div>
        <div className="italic">Portföyde bulunan aktif çeklerin toplam nominal değeri ({data.summary?.cheque_portfolio_count ?? 0} çek).</div>
      </div>
      <div className="flex flex-wrap gap-2 items-end mb-3 bg-slate-50/60 border border-slate-200 rounded p-3 text-xs">
        <Select label="Dönem" value={period} onChange={e => setPeriod(e.target.value)}>
          <option value="today">Bugün</option><option value="week">Bu Hafta</option><option value="month">Bu Ay</option>
          <option value="year">Bu Yıl</option><option value="range">Tarih Aralığı</option><option value="all">Tümü</option>
        </Select>
        {period === "range" && (<>
          <Input label="Başlangıç" type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })}/>
          <Input label="Bitiş" type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })}/>
        </>)}
        <Select label="Tür" value={type} onChange={e => setType(e.target.value)}>
          <option value="">Tümü</option><option value="INCOME">Gelir</option><option value="EXPENSE">Gider</option>
        </Select>
        <Select label="Ödeme Şekli" value={pm} onChange={e => setPm(e.target.value)}>
          <option value="">Tümü</option>
          {(constants.payment_methods_ie || []).map(p => <option key={p} value={p}>{PM_LABEL[p]}</option>)}
        </Select>
        <Select label="Kaynak" value={source} onChange={e => setSource(e.target.value)}>
          <option value="">Tümü</option>
          <option value="customer_payment">Müşteri Ödemesi</option>
          <option value="manual">Manuel</option>
        </Select>
        <Input label="Arama" value={q} onChange={e => setQ(e.target.value)} placeholder="açıklama/not..."/>
        <div className="flex-1"/>
        <Btn size="sm" onClick={() => openAdd("INCOME")} data-testid="cf-add-income"><Plus size={12}/> Gelir Ekle</Btn>
        <Btn size="sm" variant="secondary" onClick={() => openAdd("EXPENSE")} data-testid="cf-add-expense"><Plus size={12}/> Gider Ekle</Btn>
        {/* iter262 — R19 PDF trio filtre yanında */}
        <PdfActions url={`/api/accounting/income-expense/pdf?period=${period}${period==="range"&&range.start?`&start=${range.start}`:""}${period==="range"&&range.end?`&end=${range.end}`:""}${type?`&type=${type}`:""}${pm?`&payment_method=${pm}`:""}${source?`&source=${source}`:""}${q?`&q_text=${encodeURIComponent(q)}`:""}`} filename="Gelir_Gider_Raporu.pdf" label="Gelir/Gider PDF" color="cyan" testidBase="muh-r19"/>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-hidden" data-testid="cf-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-2 py-2">Tarih</th>
              <th className="text-left px-2 py-2">Tür</th>
              <th className="text-left px-2 py-2">Açıklama</th>
              <th className="text-right px-2 py-2">Tutar</th>
              <th className="text-left px-2 py-2">Ödeme Şekli</th>
              <th className="text-left px-2 py-2">Kaynak</th>
              <th className="text-left px-2 py-2">Kullanıcı</th>
              <th className="text-left px-2 py-2">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Kayıt yok.</td></tr>}
            {data.rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-2 py-1.5 font-mono text-xs">{(r.entry_date || "").slice(0, 10)}</td>
                <td className="px-2 py-1.5"><Badge level={r.type === "INCOME" ? "ok" : "missing"}>{r.type === "INCOME" ? "Gelir" : "Gider"}</Badge></td>
                <td className="px-2 py-1.5">
                  <div>{r.description}</div>
                  {r.payment_method === "CHECK" && r.check_number && (
                    <div className="text-[10px] text-slate-500 mt-0.5">🏦 {r.bank_name} #{r.check_number} · Vade: {(r.check_due_date || "").slice(0, 10)} · {r.check_company}</div>
                  )}
                  {r.note && <div className="text-[10px] text-slate-400 italic mt-0.5">{r.note}</div>}
                </td>
                <td className={`px-2 py-1.5 text-right font-mono font-semibold ${r.type === "INCOME" ? "text-emerald-700" : "text-rose-700"}`}>{money(r.amount)}</td>
                <td className="px-2 py-1.5 text-xs">{PM_LABEL[r.payment_method] || "—"}</td>
                <td className="px-2 py-1.5"><Badge level={r.source_type === "customer_payment" ? "attention" : "inactive"}>{r.source_label || "Manuel"}</Badge></td>
                <td className="px-2 py-1.5 text-xs">{r.created_by_name || "—"}</td>
                <td className="px-2 py-1.5">
                  {!r.is_projected ? (
                    <div className="flex gap-1">
                      <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`edit-ie-${r.id}`}><Edit2 size={11}/></Btn>
                      <Btn size="sm" variant="danger" onClick={() => del(r)} data-testid={`delete-ie-${r.id}`}><Trash2 size={11}/></Btn>
                    </div>
                  ) : <span className="text-[10px] text-slate-400 italic">Bakiye Takip'ten yönetilir</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {addOpen && <IEForm type={addType} editing={editing} constants={constants} onClose={() => { setAddOpen(false); setEditing(null); load(); }}/>}
      {chequeDrillOpen && <ChequePortfolioDrillModal onClose={() => setChequeDrillOpen(false)} totalHint={data.summary?.net_cheque}/>}
      {/* Faz 3 Pagination Navigasyonu — 5 yıl geriye gitmeyi kolaylaştırır */}
      <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid="cf-pagination">
        <div className="text-slate-600">
          <span className="font-mono font-semibold">{data.pagination?.total_count ?? 0}</span> kayıt · Sayfa <span className="font-mono font-semibold">{data.pagination?.page ?? 1}</span>/<span className="font-mono">{data.pagination?.total_pages ?? 1}</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-slate-500">Sayfa boyutu:</label>
          <select value={pageSize} onChange={e => setPageSize(parseInt(e.target.value, 10) || 100)} className="border rounded px-1 py-0.5 text-xs" data-testid="cf-page-size">
            <option value={50}>50</option><option value={100}>100</option><option value={200}>200</option><option value={500}>500</option>
          </select>
          <button
            className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={(data.pagination?.page ?? 1) <= 1}
            onClick={() => setPage(1)}
            data-testid="cf-page-first"
          >« İlk</button>
          <button
            className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={(data.pagination?.page ?? 1) <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            data-testid="cf-page-prev"
          >‹ Önceki</button>
          <button
            className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!(data.pagination?.has_more)}
            onClick={() => setPage(p => p + 1)}
            data-testid="cf-page-next"
          >Sonraki ›</button>
          <button
            className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={(data.pagination?.page ?? 1) >= (data.pagination?.total_pages ?? 1)}
            onClick={() => setPage(data.pagination?.total_pages || 1)}
            data-testid="cf-page-last"
          >Son »</button>
        </div>
      </div>
    </div>
  );
}

function IEForm({ type, editing, constants, onClose }) {
  const empty = { type, entry_date: todayStr(), description: "", amount: "", payment_method: "", category: type === "EXPENSE" ? "hammadde" : "", note: "", check_number: "", bank_name: "", check_due_date: "", check_company: "Meta Pharma Kozmetik" };
  const [f, setF] = useState(editing ? { ...empty, ...editing, amount: editing.amount, entry_date: (editing.entry_date || "").slice(0, 10), check_due_date: (editing.check_due_date || "").slice(0, 10) } : empty);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.description.trim()) { alert("Açıklama zorunlu"); return; }
    if (!f.amount || parseFloat(f.amount) <= 0) { alert("Tutar 0'dan büyük olmalı"); return; }
    setBusy(true);
    try {
      const payload = { ...f, amount: parseFloat(f.amount) };
      if (f.payment_method !== "CHECK") { payload.check_number = null; payload.bank_name = null; payload.check_due_date = null; payload.check_company = null; }
      if (editing) await api.put(`/accounting/income-expense/${editing.id}`, payload);
      else await api.post("/accounting/income-expense", payload);
      onClose();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title={`${editing ? "Düzenle" : "Yeni"} — ${type === "INCOME" ? "Gelir" : "Gider"}`} size="lg">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Tarih *" type="date" value={f.entry_date} onChange={e => setF({ ...f, entry_date: e.target.value })} data-testid="ie-date"/>
        <Input label="Tutar (TL) *" type="number" step="0.01" min="0" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} data-testid="ie-amount"/>
        <div className="col-span-2"><Input label="Açıklama *" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} data-testid="ie-desc" placeholder={type === "INCOME" ? "Örn. Danışmanlık geliri" : "Örn. Ağustos elektrik faturası"}/></div>
        {type === "EXPENSE" && (
          <Select label="Gider Türü" value={f.category} onChange={e => setF({ ...f, category: e.target.value })} data-testid="ie-category">
            {(constants.expense_categories || []).map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
        )}
        <Select label="Ödeme Şekli" value={f.payment_method} onChange={e => setF({ ...f, payment_method: e.target.value })} data-testid="ie-pm">
          <option value="">Seçiniz...</option>
          {(constants.payment_methods_ie || []).map(p => <option key={p} value={p}>{PM_LABEL[p]}</option>)}
        </Select>
        {f.payment_method === "CHECK" && <>
          <Input label="Çek No *" value={f.check_number} onChange={e => setF({ ...f, check_number: e.target.value })}/>
          <Input label="Banka *" value={f.bank_name} onChange={e => setF({ ...f, bank_name: e.target.value })}/>
          <Input label="Vade *" type="date" value={f.check_due_date} onChange={e => setF({ ...f, check_due_date: e.target.value })}/>
          <Select label="Çek Firması *" value={f.check_company} onChange={e => setF({ ...f, check_company: e.target.value })}>
            <option value="Meta Pharma Kozmetik">Meta Pharma Kozmetik</option>
            <option value="Pinus Kozmetik">Pinus Kozmetik</option>
            <option value="Lavion Kozmetik">Lavion Kozmetik</option>
          </Select>
        </>}
        <div className="col-span-2"><Input label="Not" value={f.note} onChange={e => setF({ ...f, note: e.target.value })}/></div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
        <Btn onClick={save} disabled={busy} data-testid="ie-save">{editing ? "Güncelle" : "Kaydet"}</Btn>
      </div>
    </Modal>
  );
}

/* ============ ORTAK BİLEŞENLER ============ */
/* ================== 4) BORÇ / ALACAK TAKİBİ ================== */
function DebtsCreditsTab() {
  const [data, setData] = useState({ credits: [], debts: [], summary: {} });
  const [creditForm, setCreditForm] = useState({ name: "", amount: "", note: "" });
  const [debtForm, setDebtForm] = useState({ name: "", amount: "", priority: "normal", note: "" });
  const [payRow, setPayRow] = useState(null); // iter127 — Ödeme Ekle
  const [ceDetailId, setCeDetailId] = useState(null); // iter128 — Company Expense Detay
  const [period, setPeriod] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const load = useCallback(async () => {
    const params = { period };
    if (period === "range") { params.start = range.start; params.end = range.end; }
    const r = await api.get("/accounting/debts-credits", params);
    setData(r);
  }, [period, range]);
  useEffect(() => { load(); }, [load]);

  const addCredit = async () => {
    if (!creditForm.name.trim() || !parseFloat(creditForm.amount)) { alert("Ad ve tutar zorunlu"); return; }
    try {
      await api.post("/accounting/debts-credits", { kind: "credit", name: creditForm.name.trim(), amount: parseFloat(creditForm.amount), note: creditForm.note || null });
      setCreditForm({ name: "", amount: "", note: "" }); load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const addDebt = async () => {
    if (!debtForm.name.trim() || !parseFloat(debtForm.amount)) { alert("Ad ve tutar zorunlu"); return; }
    try {
      await api.post("/accounting/debts-credits", { kind: "debt", name: debtForm.name.trim(), amount: parseFloat(debtForm.amount), priority: debtForm.priority, note: debtForm.note || null });
      setDebtForm({ name: "", amount: "", priority: "normal", note: "" }); load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const del = async (id) => { if (window.confirm("Silinsin mi?")) { await api.del(`/accounting/debts-credits/${id}`); load(); } };

  const priorityMeta = {
    urgent: { label: "Acil", cls: "bg-red-100 text-red-800 border-red-300" },
    normal: { label: "Normal", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    low:    { label: "Düşük", cls: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  };

  return (
    <div className="space-y-4" data-testid="debts-credits-panel">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-1">
        <div className="flex items-end gap-2">
          <Select label="Dönem" value={period} onChange={e => setPeriod(e.target.value)} data-testid="dc-period">
            <option value="all">Tümü</option><option value="today">Bugün</option><option value="week">Bu Hafta</option>
            <option value="month">Bu Ay</option><option value="year">Bu Yıl</option><option value="range">Tarih Aralığı</option>
          </Select>
          {period === "range" && (<>
            <Input label="Başlangıç" type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })}/>
            <Input label="Bitiş" type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })}/>
          </>)}
        </div>
        {/* iter262/263 — R22 Borç/Alacak PDF trio (filtreye duyarlı) */}
        <PdfActions url={`/api/accounting/debts-credits/pdf?period=${period}${period==="range"&&range.start?`&start=${range.start}`:""}${period==="range"&&range.end?`&end=${range.end}`:""}`} filename="Borc_Alacak.pdf" label="Borç/Alacak PDF" color="rose" testidBase="muh-r22"/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCard label="Toplam Alacak" value={money(data.summary?.total_credits)} color="emerald" icon={TrendingUp} testid="dc-total-credits"/>
        <StatCard label="Toplam Borç" value={money(data.summary?.total_debts)} color="rose" icon={TrendingDown} testid="dc-total-debts"/>
        <StatCard label="Borç / Alacak Net Pozisyonu" value={money(data.summary?.net_position)} color={(data.summary?.net_position || 0) >= 0 ? "emerald" : "rose"} icon={DollarSign} testid="dc-net-position"/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4" data-testid="credits-card">
          <div className="flex items-center gap-2 mb-2"><TrendingUp size={14} className="text-emerald-600"/><span className="text-sm font-bold text-slate-800">Alacaklar</span></div>
          <div className="text-[11px] text-slate-500 mb-3">Müşteri cari alacakları otomatik gelir (kaynak: Bakiye Takip) — duplicate yazılmaz. Manuel alacaklar aşağıdan eklenir.</div>
          <div className="flex flex-wrap gap-2 items-end mb-3 bg-emerald-50/40 border border-emerald-200 rounded p-2 text-xs">
            <Input label="Adı / Açıklama" value={creditForm.name} onChange={e => setCreditForm({ ...creditForm, name: e.target.value })} data-testid="credit-name"/>
            <Input label="Tutar" type="number" step="0.01" value={creditForm.amount} onChange={e => setCreditForm({ ...creditForm, amount: e.target.value })} data-testid="credit-amount"/>
            <Input label="Not" value={creditForm.note} onChange={e => setCreditForm({ ...creditForm, note: e.target.value })}/>
            <Btn onClick={addCredit} data-testid="credit-add"><Plus size={12}/> Manuel Alacak Ekle</Btn>
          </div>
          {/* iter95: user request — remove internal vertical scrollbar so the list expands naturally; page scrolls instead. Horizontal overflow preserved for narrow screens. */}
          <div className="bg-white border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase sticky top-0"><tr>
                <th className="text-left px-2 py-1">Adı / Açıklama</th>
                <th className="text-right px-2 py-1">Toplam</th>
                <th className="text-right px-2 py-1">Ödenen</th>
                <th className="text-right px-2 py-1">Kalan</th>
                <th className="text-right px-2 py-1 w-32">İşlem</th>
              </tr></thead>
              <tbody>
                {(data.credits || []).length === 0 && <tr><td colSpan={5} className="text-center py-4 text-slate-400">Alacak yok.</td></tr>}
                {(data.credits || []).map((c, i) => {
                  const rowKey = c.auto ? `auto-${c.customer_id || c.id}-${i}` : c.id;
                  const testId = c.auto ? `credit-auto-${c.customer_id || c.id}` : `credit-manual-${c.id}`;
                  return (
                    <tr key={rowKey} className="border-t border-slate-100" data-testid={testId}>
                      <td className="px-2 py-0.5">
                        <div className="flex items-center gap-1.5">
                          <span title={c.name} className="truncate">{firstTwo(c.name)}</span>
                          {c.auto ? <span className="text-[9px] bg-indigo-100 text-indigo-700 px-1 rounded" title="Müşteri cari bakiyesinden otomatik">Otomatik</span> : <span className="text-[9px] bg-slate-100 text-slate-600 px-1 rounded">Manuel</span>}
                        </div>
                      </td>
                      <td className="px-2 py-0.5 text-right font-mono text-[11px]">{money(c.gross_amount ?? c.amount)}</td>
                      <td className="px-2 py-0.5 text-right font-mono text-[11px] text-slate-600">{money(c.paid_amount || 0)}</td>
                      <td className="px-2 py-0.5 text-right font-mono font-semibold text-emerald-700">{money(c.remaining_amount ?? c.amount)}</td>
                      <td className="px-2 py-0.5 text-right">
                        <div className="flex gap-1 items-center justify-end">
                          <button
                            onClick={() => setPayRow(c)}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-semibold flex items-center gap-1 border border-emerald-300"
                            data-testid={`credit-pay-${c.id || c.customer_id}`}
                          ><Wallet size={10}/> Ödeme Ekle</button>
                          {!c.auto && <button onClick={() => del(c.id)} className="text-rose-600 hover:bg-rose-50 p-0.5 rounded" data-testid={`credit-delete-${c.id}`}><Trash2 size={11}/></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-emerald-50/40 sticky bottom-0"><tr>
                <td colSpan={3} className="px-2 py-1 font-semibold text-slate-700">Toplam Kalan Alacak</td>
                <td className="px-2 py-1 text-right font-mono font-bold text-emerald-800" data-testid="credit-total">{money(data.summary?.total_credits)}</td>
                <td></td>
              </tr></tfoot>
            </table>
          </div>
        </Card>
        <Card className="p-4" data-testid="debts-card">
          <div className="flex items-center gap-2 mb-2"><TrendingDown size={14} className="text-rose-600"/><span className="text-sm font-bold text-slate-800">Borçlar</span></div>
          <div className="text-[11px] text-slate-500 mb-3">Manuel borç kayıtları (öncelik + tutar).</div>
          <div className="flex flex-wrap gap-2 items-end mb-3 bg-rose-50/40 border border-rose-200 rounded p-2 text-xs">
            <Input label="Adı / Açıklama" value={debtForm.name} onChange={e => setDebtForm({ ...debtForm, name: e.target.value })} data-testid="debt-name"/>
            <Input label="Tutar" type="number" step="0.01" value={debtForm.amount} onChange={e => setDebtForm({ ...debtForm, amount: e.target.value })} data-testid="debt-amount"/>
            <Select label="Öncelik" value={debtForm.priority} onChange={e => setDebtForm({ ...debtForm, priority: e.target.value })} data-testid="debt-priority">
              <option value="urgent">Acil</option>
              <option value="normal">Normal</option>
              <option value="low">Düşük</option>
            </Select>
            <Input label="Not" value={debtForm.note} onChange={e => setDebtForm({ ...debtForm, note: e.target.value })}/>
            <Btn onClick={addDebt} data-testid="debt-add"><Plus size={12}/> Borç Ekle</Btn>
          </div>
          {/* iter95: user request — remove internal vertical scrollbar so the list expands naturally; page scrolls instead. */}
          <div className="bg-white border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600 text-[10px] uppercase sticky top-0"><tr>
                <th className="text-left px-2 py-1">Adı / Açıklama</th>
                <th className="text-left px-2 py-1">Öncelik</th>
                <th className="text-right px-2 py-1">Toplam</th>
                <th className="text-right px-2 py-1">Ödenen</th>
                <th className="text-right px-2 py-1">Kalan</th>
                <th className="text-right px-2 py-1 w-32">İşlem</th>
              </tr></thead>
              <tbody>
                {(data.debts || []).length === 0 && <tr><td colSpan={6} className="text-center py-4 text-slate-400">Borç yok.</td></tr>}
                {(data.debts || []).map(d => {
                  const meta = priorityMeta[d.priority] || priorityMeta.normal;
                  const isAuto = !!d.auto;
                  const isCE = d.source === "company_expense";
                  return (
                    <tr key={d.id} className="border-t border-slate-100" data-testid={isCE ? `debt-ce-${d.id}` : (isAuto ? `debt-auto-${d.id}` : `debt-${d.id}`)}>
                      <td className="px-2 py-0.5">
                        <div className="flex items-center gap-1.5">
                          <span title={d.name} className="truncate">{firstTwo(d.name)}</span>
                          {isCE
                            ? <span className="text-[9px] bg-purple-100 text-purple-700 px-1 rounded" title="Şirket Genel Gideri (aylık birikimli)">Şirket Gid.</span>
                            : (isAuto ? <span className="text-[9px] bg-indigo-100 text-indigo-700 px-1 rounded" title="Tedarikçi alım borcundan otomatik">Otomatik</span> : <span className="text-[9px] bg-slate-100 text-slate-600 px-1 rounded">Manuel</span>)}
                          {isCE && d.obligation_count > 0 && <span className="text-[9px] bg-slate-200 text-slate-700 px-1 rounded" title="Açık aylık borç sayısı">{d.obligation_count} ay</span>}
                        </div>
                        {d.note && <span className="ml-1 text-[9px] text-slate-500 italic">({d.note})</span>}
                        {isCE && d.supplier_name && <div className="text-[9px] text-slate-500" title={d.supplier_name}>Tedarikçi: {firstTwo(d.supplier_name)}</div>}
                      </td>
                      <td className="px-2 py-0.5"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${meta.cls}`} data-testid={`debt-priority-${d.id}`}>{meta.label}</span></td>
                      <td className="px-2 py-0.5 text-right font-mono text-[11px]">{money(d.gross_amount ?? d.amount)}</td>
                      <td className="px-2 py-0.5 text-right font-mono text-[11px] text-slate-600">{money(d.paid_amount || 0)}</td>
                      <td className="px-2 py-0.5 text-right font-mono font-semibold text-rose-700">{money(d.remaining_amount ?? d.amount)}</td>
                      <td className="px-2 py-0.5 text-right">
                        <div className="flex gap-1 items-center justify-end">
                          {isCE && (
                            <button
                              onClick={() => setCeDetailId(d.id)}
                              className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 hover:bg-indigo-200 text-indigo-800 font-semibold flex items-center gap-1 border border-indigo-300"
                              data-testid={`debt-ce-detail-${d.id}`}
                            >Detay</button>
                          )}
                          <button
                            onClick={() => setPayRow(d)}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-semibold flex items-center gap-1 border border-emerald-300"
                            data-testid={`debt-pay-${d.id}`}
                          ><Wallet size={10}/> Ödeme Ekle</button>
                          {!isAuto && <button onClick={() => del(d.id)} className="text-rose-600 hover:bg-rose-50 p-0.5 rounded" data-testid={`debt-delete-${d.id}`}><Trash2 size={11}/></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-rose-50/40 sticky bottom-0"><tr>
                <td colSpan={4} className="px-2 py-1 font-semibold text-slate-700">Toplam Kalan Borç</td>
                <td className="px-2 py-1 text-right font-mono font-bold text-rose-800" data-testid="debt-total">{money(data.summary?.total_debts)}</td>
                <td></td>
              </tr></tfoot>
            </table>
          </div>
        </Card>
      </div>
      {payRow && (
        <AddDCPaymentModal
          row={payRow}
          onClose={() => setPayRow(null)}
          onSaved={() => { setPayRow(null); load(); }}
        />
      )}
      {ceDetailId && (
        <CompanyExpenseDetailModal
          expenseId={ceDetailId}
          onClose={() => setCeDetailId(null)}
        />
      )}
    </div>
  );
}

/* iter128 — Şirket Genel Gideri aylık borç detay modal */
function CompanyExpenseDetailModal({ expenseId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/accounting/debts-credits/company-expense/${expenseId}/detail`);
        setData(r);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Detay yüklenemedi");
      }
    })();
  }, [expenseId]);
  const TR_MONTHS = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  const badge = (s) => {
    if (s === "ödendi") return "bg-emerald-100 text-emerald-800 border-emerald-300";
    if (s === "kısmi") return "bg-amber-100 text-amber-800 border-amber-300";
    return "bg-rose-100 text-rose-800 border-rose-300";
  };
  return (
    <Modal open={true} onClose={onClose} title="ŞİRKET GİDERİ — AYLIK BORÇ DETAYI" size="lg">
      {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="ce-detail-err">{err}</div>}
      {!data ? (!err && <div className="text-sm text-slate-500 py-4 text-center">Yükleniyor...</div>) : (
        <div className="space-y-3" data-testid="ce-detail-modal">
          <div className="bg-purple-50/60 border border-purple-200 rounded p-2 text-xs">
            <b>{data.expense.name}</b> · Aylık Tutar: <span className="font-mono">{money(data.expense.amount)}</span>
            {data.expense.own_company && <span className="ml-2 text-slate-500">· {data.expense.own_company}</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Toplam Borç</div><div className="font-mono font-bold" data-testid="ce-detail-total">{money(data.summary.total_debt)}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Toplam Ödenen</div><div className="font-mono" data-testid="ce-detail-paid">{money(data.summary.total_paid)}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Kalan</div><div className="font-mono font-bold text-rose-700" data-testid="ce-detail-remaining">{money(data.summary.remaining)}</div></div>
          </div>
          <div className="bg-white border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-xs uppercase"><tr>
                <th className="text-left px-2 py-2">Ay</th>
                <th className="text-right px-2 py-2">Borç</th>
                <th className="text-right px-2 py-2">Ödenen</th>
                <th className="text-right px-2 py-2">Kalan</th>
                <th className="text-center px-2 py-2">Vade</th>
                <th className="text-center px-2 py-2">Durum</th>
              </tr></thead>
              <tbody>
                {(data.obligations || []).length === 0 && <tr><td colSpan={6} className="text-center py-4 text-slate-400">Henüz aylık borç oluşmadı</td></tr>}
                {(data.obligations || []).map(o => (
                  <tr key={o.id} className="border-t border-slate-100" data-testid={`ce-obligation-${o.year}-${o.month}`}>
                    <td className="px-2 py-1.5">{TR_MONTHS[(o.month || 1) - 1]} {o.year}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs">{money(o.gross_amount)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs">{money(o.paid_amount)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold">{money(o.remaining_amount)}</td>
                    <td className="px-2 py-1.5 text-center text-xs font-mono">{(o.due_date || "").slice(5)}</td>
                    <td className="px-2 py-1.5 text-center"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${badge(o.status)}`}>{o.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end pt-2 border-t border-slate-200">
            <Btn variant="ghost" onClick={onClose}>Kapat</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}


function StatCard({ label, value, color = "indigo", icon: Icon, testid, onClick }) {
  const themes = {
    indigo: "from-indigo-50 to-indigo-100/60 border-indigo-200 text-indigo-800",
    emerald: "from-emerald-50 to-emerald-100/60 border-emerald-200 text-emerald-800",
    rose: "from-rose-50 to-rose-100/60 border-rose-200 text-rose-800",
    amber: "from-amber-50 to-amber-100/60 border-amber-200 text-amber-800",
  };
  const interactive = typeof onClick === "function";
  return (
    <div
      className={`rounded-lg p-3 bg-gradient-to-br ${themes[color] || themes.indigo} border ${interactive ? "cursor-pointer hover:shadow-md hover:brightness-105 transition" : ""}`}
      data-testid={testid}
      onClick={interactive ? onClick : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase font-semibold tracking-wider opacity-70">{label}</div>
        {Icon && <Icon size={14} className="opacity-70"/>}
      </div>
      <div className="text-lg font-bold font-mono">{value}</div>
      {interactive && <div className="text-[9px] mt-0.5 opacity-60">Detay için tıkla →</div>}
    </div>
  );
}

/* ================================================================
   iter101 VADE MALİYETİ TAB — Analitik finansman maliyeti (Admin-only)
   Backend: /api/accounting/vade-maliyeti/{summary,customer/:id,rate}
   Cheque lifecycle: /api/accounting/cheque/:tid/{transfer-to-supplier,collect,reset-lifecycle}
================================================================ */
const pct = (r) => `%${(Number(r || 0) * 100).toFixed(2).replace(".", ",")}`;

function VadeMaliyetiTab() {
  const [data, setData] = useState({ rate: 0.055, summary: {}, customers: [] });
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("burden_desc");
  const [selected, setSelected] = useState(null);
  const [rateOpen, setRateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/accounting/vade-maliyeti/summary");
      setData(r);
    } catch (e) {
      setErr(e?.message || "Yüklenemedi");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    let arr = [...(data.customers || [])];
    if (q.trim()) {
      const qt = q.toLocaleLowerCase("tr");
      arr = arr.filter(r => (r.customer_name || "").toLocaleLowerCase("tr").includes(qt));
    }
    arr.sort((a, b) => {
      if (sortBy === "burden_desc") return b.total_burden - a.total_burden;
      if (sortBy === "burden_asc") return a.total_burden - b.total_burden;
      if (sortBy === "open_desc") return b.open_balance - a.open_balance;
      if (sortBy === "avg_days_desc") return b.avg_collection_days - a.avg_collection_days;
      return (a.customer_name || "").localeCompare(b.customer_name || "", "tr");
    });
    return arr;
  }, [data.customers, q, sortBy]);

  const s = data.summary || {};
  return (
    <div>
      {/* Info banner */}
      <div className="mb-3 p-3 rounded-lg bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 text-xs text-indigo-800 flex items-start gap-2" data-testid="vm-info-banner">
        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
        <div>
          <b>Vade Maliyeti = Analitik finansman fırsat maliyeti.</b> Formül: Tutar × ((1+oran)^(gün/30) − 1).
          Bu değerler <b>ledger'a gider yazmaz</b>; sadece geç tahsilatın şirkete olan ekonomik yükünü ölçer.
          Aylık oran: <b>{pct(data.rate)}</b> · Yalnız Sistem Yöneticisi görür.
        </div>
        <button onClick={() => setRateOpen(true)} className="ml-auto text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 flex items-center gap-1 whitespace-nowrap" data-testid="vm-rate-open">
          <Settings size={12}/> Oran Ayarı
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <StatCard label="Toplam Yük" value={money(s.total_burden)} color="rose" icon={DollarSign} testid="vm-kpi-burden"/>
        <StatCard label="Açık Maliyet" value={money(s.total_open)} color="amber" icon={Clock} testid="vm-kpi-open"/>
        <StatCard label="Gerçekleşen" value={money(s.total_realized)} color="indigo" icon={TrendingDown} testid="vm-kpi-realized"/>
        <StatCard label="Çek Nominal" value={money(s.total_nominal_cheque)} color="indigo" icon={Percent} testid="vm-kpi-nominal-cheque"/>
        <StatCard label="Önlenen (Ciro/Faktoring)" value={money(s.total_avoided)} color="emerald" icon={CheckCircle2} testid="vm-kpi-avoided"/>
        <StatCard label="Ort. Tahsilat Günü" value={`${s.avg_collection_days_weighted || 0} gün`} color="indigo" icon={Clock} testid="vm-kpi-avg-days"/>
      </div>
      {s.top_customer && (
        <div className="mb-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid="vm-top-customer">
          En yüksek finansman yükü: <b className="text-rose-700">{s.top_customer}</b> ({money(s.top_customer_burden)})
        </div>
      )}
      {/* iter328 — Çek vade tarihi eksik uyarısı */}
      {s.missing_cheque_due_count > 0 && (
        <div className="mb-3 text-xs bg-amber-50 border border-amber-300 rounded px-3 py-2 flex items-start gap-2" data-testid="vm-missing-cheque-due">
          <AlertTriangle size={13} className="mt-0.5 text-amber-700 flex-shrink-0"/>
          <div className="text-amber-900">
            <b>Çek vade tarihi bulunamadı</b> ({s.missing_cheque_due_count} kayıt).
            Bu çek ödemeleri için Ortalama Vade Günü ve Toplam Yük
            hesaplamasında güvenli fallback kullanıldı — doğru sonuç için
            ilgili tahsilat kayıtlarına çek vade tarihi girilmelidir.
          </div>
        </div>
      )}

      {/* Filter row */}
      <div className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 mb-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pl-0.5">Arama</label>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-2.5 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Müşteri..." className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded" data-testid="vm-search"/>
          </div>
        </div>
        <Select label="Sıralama" value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="vm-sort">
          <option value="burden_desc">En Yüksek Toplam Yük</option>
          <option value="burden_asc">En Düşük Toplam Yük</option>
          <option value="open_desc">En Yüksek Açık Bakiye</option>
          <option value="avg_days_desc">En Uzun Tahsilat Günü</option>
          <option value="name">Müşteri Adı</option>
        </Select>
        <Btn onClick={load} disabled={loading} data-testid="vm-refresh">Yenile</Btn>
      </div>

      {/* Table */}
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 mb-2" data-testid="vm-err">{err}</div>}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="vm-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Müşteri</th>
              <th className="text-right px-3 py-2">Alacak</th>
              <th className="text-right px-3 py-2">Tahsil</th>
              <th className="text-right px-3 py-2">Açık</th>
              <th className="text-right px-3 py-2">Ort. Gün</th>
              <th className="text-right px-3 py-2">Gerçekleşen</th>
              <th className="text-right px-3 py-2">Açık Maliyet</th>
              <th className="text-right px-3 py-2">Önlenen</th>
              <th className="text-right px-3 py-2 bg-rose-50">Toplam Yük</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400">{loading ? "Yükleniyor..." : "Kayıt yok."}</td></tr>}
            {rows.map(r => (
              <tr key={r.customer_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(r)} data-testid={`vm-row-${r.customer_id}`}>
                <td className="px-3 py-2 font-semibold text-indigo-700">{r.customer_name}</td>
                <td className="px-3 py-2 text-right font-mono">{money(r.total_receivable)}</td>
                <td className="px-3 py-2 text-right font-mono">{money(r.total_paid)}</td>
                <td className="px-3 py-2 text-right font-mono">{money(r.open_balance)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{r.avg_collection_days}</td>
                <td className="px-3 py-2 text-right font-mono text-indigo-800">{money(r.realized_financing_cost)}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-800">{money(r.open_financing_cost)}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-800">{money(r.avoided_financing_cost)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-rose-800 bg-rose-50/40" data-testid={`vm-burden-${r.customer_id}`}>{money(r.total_burden)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <VadeMaliyetiDetailModal customer={selected} rate={data.rate} onClose={() => { setSelected(null); load(); }}/>}
      {rateOpen && <FinancingRateModal currentRate={data.rate} onClose={() => setRateOpen(false)} onSaved={() => { setRateOpen(false); load(); }}/>}
    </div>
  );
}

function VadeMaliyetiDetailModal({ customer, rate, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await api.get(`/accounting/vade-maliyeti/customer/${customer.customer_id}`);
      setData(r);
    } catch (e) { setErr(e?.message || "Yüklenemedi"); }
  }, [customer.customer_id]);
  useEffect(() => { load(); }, [load]);

  const chequeAction = async (paymentId, action, payload) => {
    setBusy(true); setErr("");
    try {
      await api.post(`/accounting/cheque/${paymentId}/${action}`, payload || {});
      await load();
    } catch (e) { setErr(e?.message || "İşlem başarısız"); }
    finally { setBusy(false); }
  };
  const onTransfer = async (paymentId) => {
    const dt = window.prompt("Tedarikçiye ciro tarihi (YYYY-MM-DD)?", todayStr());
    if (!dt) return;
    const sup = window.prompt("Tedarikçi adı (opsiyonel)?", "") || null;
    await chequeAction(paymentId, "transfer-to-supplier", { transferred_to_supplier_at: dt, supplier_name: sup });
  };
  const onCollect = async (paymentId) => {
    const dt = window.prompt("Tahsilat tarihi (YYYY-MM-DD)?", todayStr());
    if (!dt) return;
    await chequeAction(paymentId, "collect", { actual_collection_date: dt });
  };
  const onReset = async (paymentId) => {
    if (!window.confirm("Çek yaşam döngüsü sıfırlanacak (ciro/tahsilat işareti kaldırılır). Devam?")) return;
    await chequeAction(paymentId, "reset-lifecycle");
  };

  return (
    <Modal open={true} onClose={onClose} title={`Vade Maliyeti — ${customer.customer_name}`} size="xl">
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 mb-2" data-testid="vm-detail-err">{err}</div>}
      {!data && <div className="text-center py-8 text-slate-400">Yükleniyor...</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <StatCard label="Toplam Alacak" value={money(data.total_receivable)} color="rose" icon={DollarSign}/>
            <StatCard label="Toplam Tahsilat" value={money(data.total_paid)} color="emerald" icon={Wallet}/>
            <StatCard label="Açık Bakiye" value={money(data.open_balance)} color="amber" icon={Clock}/>
            <StatCard label="Toplam Yük" value={money(data.total_burden)} color="rose" icon={TrendingDown} testid="vm-detail-burden"/>
          </div>
          <div className="text-[11px] text-slate-500 italic mb-2">
            Aylık oran anlık: {pct(data.rate ?? rate)} — segment başına <b>rate_snapshot</b> saklı olduğu için oran değişse bile geçmiş kayıtlar bozulmaz.
          </div>
          <div className="bg-white border border-slate-200 rounded overflow-x-auto" data-testid="vm-detail-table">
            <table className="w-full text-sm min-w-[1100px]">
              <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-2 py-2">Başlangıç</th>
                  <th className="text-left px-2 py-2">Nominal Bitiş</th>
                  <th className="text-left px-2 py-2">Gerçek Bitiş</th>
                  <th className="text-left px-2 py-2">Sebep</th>
                  <th className="text-right px-2 py-2">Tutar</th>
                  <th className="text-right px-2 py-2">Nom. Gün</th>
                  <th className="text-right px-2 py-2">Gerç. Gün</th>
                  <th className="text-right px-2 py-2">Nominal ₺</th>
                  <th className="text-right px-2 py-2">Gerçekleşen ₺</th>
                  <th className="text-right px-2 py-2">Önlenen ₺</th>
                  <th className="text-left px-2 py-2">Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {(data.segments || []).length === 0 && <tr><td colSpan={11} className="text-center py-6 text-slate-400">Segment yok.</td></tr>}
                {(data.segments || []).map((sg, idx) => (
                  <tr key={`${sg.sale_id}-${sg.payment_id || idx}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-2 py-2 text-xs font-mono">{(sg.start_date || "").slice(0, 10)}</td>
                    <td className="px-2 py-2 text-xs font-mono">{(sg.nominal_end || "—").slice(0, 10)}</td>
                    <td className="px-2 py-2 text-xs font-mono">{(sg.realized_end || "—").slice(0, 10)}</td>
                    <td className="px-2 py-2 text-[11px]"><Badge level={sg.status === "OPEN" ? "attention" : sg.is_cheque ? "critical" : "ok"}>{sg.end_reason}</Badge></td>
                    <td className="px-2 py-2 text-right font-mono">{money(sg.amount)}</td>
                    <td className="px-2 py-2 text-right font-mono text-xs">{sg.nominal_days}</td>
                    <td className="px-2 py-2 text-right font-mono text-xs">{sg.realized_days}</td>
                    <td className="px-2 py-2 text-right font-mono text-indigo-800">{money(sg.nominal_cost)}</td>
                    <td className={`px-2 py-2 text-right font-mono ${sg.status === "OPEN" ? "text-amber-800" : "text-indigo-800"}`}>{money(sg.status === "OPEN" ? sg.nominal_cost : sg.realized_cost)}</td>
                    <td className="px-2 py-2 text-right font-mono text-emerald-800">{money(sg.avoided_cost)}</td>
                    <td className="px-2 py-2">
                      {sg.is_cheque && sg.payment_id && (
                        <div className="flex gap-1 flex-wrap">
                          {!sg.transferred_at && !sg.collected_at && (
                            <>
                              <button onClick={() => onTransfer(sg.payment_id)} disabled={busy} className="text-[10px] px-2 py-1 rounded bg-indigo-100 hover:bg-indigo-200 text-indigo-800 flex items-center gap-1" data-testid={`vm-cheque-transfer-${sg.payment_id}`}>
                                <ArrowRightLeft size={10}/> Ciro
                              </button>
                              <button onClick={() => onCollect(sg.payment_id)} disabled={busy} className="text-[10px] px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 flex items-center gap-1" data-testid={`vm-cheque-collect-${sg.payment_id}`}>
                                <CheckCircle2 size={10}/> Tahsil
                              </button>
                            </>
                          )}
                          {(sg.transferred_at || sg.collected_at) && (
                            <button onClick={() => onReset(sg.payment_id)} disabled={busy} className="text-[10px] px-2 py-1 rounded bg-slate-100 hover:bg-rose-100 text-slate-700 hover:text-rose-700 flex items-center gap-1" data-testid={`vm-cheque-reset-${sg.payment_id}`}>
                              <RotateCcw size={10}/> Sıfırla
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

function FinancingRateModal({ currentRate, onClose, onSaved }) {
  const [pctInput, setPctInput] = useState(((Number(currentRate || 0) * 100)).toFixed(2));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    const r = parseFloat(String(pctInput).replace(",", ".")) / 100;
    if (isNaN(r) || r < 0 || r > 1) { setErr("Oran 0 ile 100 arasında olmalı (örn. 5.5)"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/accounting/vade-maliyeti/rate", { financing_monthly_rate: r });
      onSaved();
    } catch (e) { setErr(e?.message || "Kayıt başarısız"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title="Aylık Finansman Oranı" size="md">
      <div className="space-y-3">
        <div className="text-xs text-slate-600 bg-indigo-50 border border-indigo-200 rounded p-2">
          Bu oran <b>tüm gelecek Vade Maliyeti segmentleri</b> için kullanılır. Geçmiş segmentler kendi <b>rate_snapshot</b>'ı ile hesaplanmıştır — oran değişikliği eski kayıtları etkilemez.
        </div>
        <Input label="Aylık Oran (%)" value={pctInput} onChange={e => setPctInput(e.target.value)} placeholder="5.5" data-testid="vm-rate-input"/>
        {err && <div className="text-xs text-rose-700">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Btn onClick={onClose} variant="ghost">İptal</Btn>
          <Btn onClick={save} disabled={busy} className="bg-indigo-600 text-white hover:bg-indigo-700" data-testid="vm-rate-save">
            {busy ? "Kaydediliyor..." : "Kaydet"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}


/* ============================================================
   iter121 — BASİT FAKTORİNG TAKİBİ
   Muhasebe → Faktoring sekmesi.
   Mevcut çeklerden (PORTFÖYDE) seçim + kesinti + net alınan.
   Yeni bir çek kopyası yaratılmaz; cheque_id FK ile mevcut kayıt kullanılır.
============================================================ */
const FX_STATUS_BADGE = {
  "BEKLİYOR": "bg-amber-100 text-amber-800 border-amber-300",
  "ÖDEME ALINDI": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "İPTAL": "bg-slate-100 text-slate-500 border-slate-300 line-through",
};

function FactoringTab() {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [period, setPeriod] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const [newOpen, setNewOpen] = useState(false);
  const [collectFor, setCollectFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { period };
      if (period === "range") { params.start = range.start; params.end = range.end; }
      const r = await api.get("/accounting/factoring", params);
      setData(r || { rows: [], summary: {} });
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  }, [period, range]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    let arr = [...(data.rows || [])];
    if (statusFilter !== "all") arr = arr.filter(r => r.status === statusFilter);
    if (q.trim()) {
      const qt = q.toLocaleLowerCase("tr");
      arr = arr.filter(r => {
        const hay = `${r.cheque?.customer_name || ""} ${r.cheque?.check_number || ""} ${r.cheque?.bank_name || ""} ${r.factoring_company || ""}`.toLocaleLowerCase("tr");
        return hay.includes(qt);
      });
    }
    return arr;
  }, [data.rows, statusFilter, q]);

  const s = data.summary || {};

  return (
    <div data-testid="factoring-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Toplam Çek Tutarı" value={money(s.total_cheque_amount)} color="indigo" icon={Banknote} testid="fx-stat-total-cheque"/>
        <StatCard label="Net Alınan" value={money(s.total_net_received)} color="emerald" icon={TrendingUp} testid="fx-stat-net-received"/>
        <StatCard label="Toplam Gider (Kesinti)" value={money(s.total_deduction)} color="rose" icon={TrendingDown} testid="fx-stat-deduction"/>
        <StatCard label="Bekleyen / Alınan" value={`${s.waiting_count || 0} / ${s.collected_count || 0}`} color="amber" icon={Clock} testid="fx-stat-count"/>
      </div>

      <div className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 mb-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pl-0.5">Arama</label>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-2.5 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Müşteri, çek no, banka..." className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded" data-testid="fx-search"/>
          </div>
        </div>
        <Select label="Durum" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="fx-status-filter">
          <option value="all">Tümü</option>
          <option value="BEKLİYOR">Bekliyor</option>
          <option value="ÖDEME ALINDI">Ödeme Alındı</option>
        </Select>
        <Btn
          onClick={() => setNewOpen(true)}
          className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-bold px-3 py-1.5 shadow"
          data-testid="fx-new-btn"
        >
          <PlusCircle size={14}/> Yeni Faktoring
        </Btn>
        <Select label="Dönem" value={period} onChange={e => setPeriod(e.target.value)} data-testid="fx-period">
          <option value="all">Tümü</option><option value="today">Bugün</option><option value="week">Bu Hafta</option>
          <option value="month">Bu Ay</option><option value="year">Bu Yıl</option><option value="range">Tarih Aralığı</option>
        </Select>
        {period === "range" && (<>
          <Input label="Başlangıç" type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })}/>
          <Input label="Bitiş" type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })}/>
        </>)}
        {/* iter262/263 — R24 Faktoring PDF trio (durum + dönem duyarlı) */}
        <PdfActions url={`/api/accounting/factoring-report/pdf?period=${period}${period==="range"&&range.start?`&start=${range.start}`:""}${period==="range"&&range.end?`&end=${range.end}`:""}${statusFilter!=="all"?`&status=${encodeURIComponent(statusFilter)}`:""}`} filename="Faktoring.pdf" label="Faktoring PDF" color="fuchsia" testidBase="muh-r24"/>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="fx-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Faktoring Tarihi</th>
              <th className="text-left px-3 py-2">Müşteri / Çek</th>
              <th className="text-left px-3 py-2">Banka · Vade</th>
              <th className="text-right px-3 py-2">Tutar</th>
              <th className="text-right px-3 py-2">Kesinti</th>
              <th className="text-right px-3 py-2">Net</th>
              <th className="text-center px-3 py-2">Durum</th>
              <th className="text-left px-3 py-2">Ödeme Tarihi</th>
              <th className="text-center px-3 py-2">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400">{busy ? "Yükleniyor..." : "Henüz faktoring kaydı yok."}</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fx-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.factoring_date}</td>
                <td className="px-3 py-2">
                  <div className="font-semibold text-slate-800">{r.cheque?.customer_name || "—"}</div>
                  <div className="text-[11px] text-slate-500 font-mono">Çek #{r.cheque?.check_number || "—"}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  <div className="text-slate-700">{r.cheque?.bank_name || "—"}</div>
                  <div className="text-slate-500 font-mono">Vade: {(r.cheque?.check_due_date || "—").slice(0,10)}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono">{money(r.amount)}</td>
                <td className="px-3 py-2 text-right font-mono text-rose-700">{money(r.deduction)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700" data-testid={`fx-net-${r.id}`}>{money(r.net_amount)}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${FX_STATUS_BADGE[r.status] || "bg-slate-100 text-slate-700"}`} data-testid={`fx-status-${r.id}`}>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono">{r.collected_at || "—"}</td>
                <td className="px-3 py-2 text-center">
                  <div className="flex gap-1 justify-center">
                    {r.status === "BEKLİYOR" && (
                      <button
                        onClick={() => setCollectFor(r)}
                        className="text-[10px] px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 flex items-center gap-1"
                        data-testid={`fx-collect-${r.id}`}
                      >
                        <CheckCircle2 size={10}/> Ödeme Alındı
                      </button>
                    )}
                    <button
                      onClick={() => setCancelFor(r)}
                      className="text-[10px] px-2 py-1 rounded bg-slate-100 hover:bg-rose-100 text-slate-700 hover:text-rose-700 flex items-center gap-1"
                      data-testid={`fx-cancel-${r.id}`}
                    >
                      <X size={10}/> İptal
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-[11px] text-slate-500 bg-violet-50/60 border border-violet-200 rounded p-2">
        <b>Not:</b> Ödeme alındığında sadece <b>Faktoring Kesintisi</b> otomatik olarak Muhasebe → Gelir/Gider'e
        "Faktoring Gideri" kategorisinde EXPENSE olarak yazılır (net tutar tekrar gelir yazılmaz — çift sayım yok).
        Orijinal çek vade tarihi <b>DEĞİŞMEZ</b>; çek Çek Takviminde görünmeye devam eder.
      </div>

      {newOpen && <NewFactoringModal onClose={() => setNewOpen(false)} onSaved={() => { setNewOpen(false); load(); }}/>}
      {collectFor && <CollectFactoringModal fx={collectFor} onClose={() => setCollectFor(null)} onSaved={() => { setCollectFor(null); load(); }}/>}
      {cancelFor && <CancelFactoringModal fx={cancelFor} onClose={() => setCancelFor(null)} onSaved={() => { setCancelFor(null); load(); }}/>}
    </div>
  );
}

function NewFactoringModal({ onClose, onSaved }) {
  const [cheques, setCheques] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({
    cheque_id: "",
    factoring_date: todayStr(),
    deduction: "",
    factoring_company: "",
    note: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/accounting/factoring/available-cheques");
        setCheques(r?.cheques || []);
      } catch (e) { setErr(e?.message || "Çekler yüklenemedi"); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return cheques.slice(0, 30);
    const qt = q.toLocaleLowerCase("tr");
    return cheques.filter(c =>
      (`${c.customer_name} ${c.check_number} ${c.bank_name}`).toLocaleLowerCase("tr").includes(qt)
    ).slice(0, 30);
  }, [cheques, q]);

  const selected = cheques.find(c => c.id === form.cheque_id);
  const amount = Number(selected?.amount || 0);
  const deduction = Number(String(form.deduction || "0").replace(",", ".")) || 0;
  const netAmount = Math.max(0, amount - deduction);

  const save = async () => {
    if (!form.cheque_id) { setErr("Faktoringe verilecek çeki seçin"); return; }
    if (!form.factoring_date) { setErr("Faktoring tarihi zorunlu"); return; }
    if (deduction < 0) { setErr("Kesinti negatif olamaz"); return; }
    if (deduction > amount + 0.005) { setErr("Kesinti çek tutarından büyük olamaz"); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/accounting/factoring", {
        cheque_id: form.cheque_id,
        factoring_date: form.factoring_date,
        deduction,
        factoring_company: form.factoring_company || null,
        note: form.note || null,
      });
      onSaved();
    } catch (e) { setErr(e?.message || "Kayıt başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title="Yeni Faktoring" size="lg">
      <div className="space-y-3">
        <div className="text-xs text-slate-600 bg-violet-50 border border-violet-200 rounded p-2">
          Faktoringe verilecek çek portföydeki (henüz tedarikçiye ciro edilmemiş, tahsil edilmemiş) çekler arasından seçilir.
          Yeni bir çek kaydı oluşturulmaz — mevcut çekin durumu <b>FAKTORİNGDE</b> olarak işaretlenir.
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pl-0.5">Portföydeki Çek Seç</label>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-2.5 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Müşteri / çek no / banka..." className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded" data-testid="fx-new-search"/>
          </div>
          <div className="max-h-52 overflow-auto border border-slate-200 rounded mt-1" data-testid="fx-new-cheque-list">
            {filtered.length === 0 && <div className="p-3 text-center text-xs text-slate-400">Portföyde uygun çek yok.</div>}
            {filtered.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => setForm(f => ({ ...f, cheque_id: c.id }))}
                className={`w-full text-left px-2 py-1.5 text-xs border-b border-slate-100 hover:bg-violet-50 ${form.cheque_id === c.id ? "bg-violet-100" : ""}`}
                data-testid={`fx-new-cheque-${c.id}`}
              >
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-800">{c.customer_name}</span>
                  <span className="font-mono text-emerald-700">{money(c.amount)}</span>
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  Çek #{c.check_number} · {c.bank_name} · Vade {c.check_due_date || "—"}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input type="date" label="Faktoring Tarihi" value={form.factoring_date} onChange={e => setForm(f => ({ ...f, factoring_date: e.target.value }))} data-testid="fx-new-date"/>
          <Input label="Faktoring Firması (opsiyonel)" value={form.factoring_company} onChange={e => setForm(f => ({ ...f, factoring_company: e.target.value }))} data-testid="fx-new-company"/>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50/60 border border-slate-200 rounded p-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Çek Tutarı</label>
            <div className="font-mono text-lg text-slate-800" data-testid="fx-new-amount">{money(amount)}</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Faktoring Kesintisi</label>
            <input
              type="number"
              step="0.01"
              value={form.deduction}
              onChange={e => setForm(f => ({ ...f, deduction: e.target.value }))}
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono text-rose-700"
              placeholder="0.00"
              data-testid="fx-new-deduction"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Net Alınan</label>
            <div className="font-mono text-lg font-bold text-emerald-700" data-testid="fx-new-net">{money(netAmount)}</div>
          </div>
        </div>

        <Textarea label="Not (opsiyonel)" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} data-testid="fx-new-note"/>

        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="fx-new-err">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <Btn onClick={onClose} variant="ghost" data-testid="fx-new-cancel">İptal</Btn>
          <Btn onClick={save} disabled={busy || !form.cheque_id} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="fx-new-save">
            {busy ? "Kaydediliyor..." : "Faktoringe Ver"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function CollectFactoringModal({ fx, onClose, onSaved }) {
  const [collectedAt, setCollectedAt] = useState(todayStr());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!collectedAt) { setErr("Ödeme tarihi zorunlu"); return; }
    setBusy(true); setErr("");
    try {
      await api.put(`/accounting/factoring/${fx.id}/collect`, {
        collected_at: collectedAt,
        note: note || null,
      });
      onSaved();
    } catch (e) { setErr(e?.message || "Kayıt başarısız"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title="Faktoring Ödemesi Alındı" size="md">
      <div className="space-y-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs space-y-1">
          <div><b>Müşteri:</b> {fx.cheque?.customer_name}</div>
          <div><b>Çek No:</b> {fx.cheque?.check_number} · {fx.cheque?.bank_name}</div>
          <div><b>Çek Tutarı:</b> {money(fx.amount)}</div>
          <div><b>Kesinti:</b> <span className="text-rose-700 font-mono">{money(fx.deduction)}</span></div>
          <div><b>Net Alınacak:</b> <span className="text-emerald-700 font-mono font-bold">{money(fx.net_amount)}</span></div>
        </div>
        <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
          Onayla ile birlikte:
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            <li>Faktoring durumu <b>ÖDEME ALINDI</b> olur.</li>
            <li>Çek durumu <b>FAKTORİNG İLE TAHSİL EDİLDİ</b> olarak işaretlenir.</li>
            <li>Sadece <b>kesinti tutarı</b> ({money(fx.deduction)}) otomatik olarak Gelir/Gider'e "Faktoring Gideri" olarak yazılır.</li>
          </ul>
        </div>
        <Input type="date" label="Ödeme Alındığı Tarih" value={collectedAt} onChange={e => setCollectedAt(e.target.value)} data-testid="fx-collect-date"/>
        <Textarea label="Not (opsiyonel)" value={note} onChange={e => setNote(e.target.value)} rows={2} data-testid="fx-collect-note"/>
        {err && <div className="text-xs text-rose-700" data-testid="fx-collect-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Btn onClick={onClose} variant="ghost" data-testid="fx-collect-cancel">Vazgeç</Btn>
          <Btn onClick={save} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="fx-collect-save">
            {busy ? "Kaydediliyor..." : "Ödemeyi Onayla"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function CancelFactoringModal({ fx, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!reason.trim()) { setErr("İptal gerekçesi zorunlu"); return; }
    setBusy(true); setErr("");
    try {
      await api.put(`/accounting/factoring/${fx.id}/cancel`, { reason: reason.trim() });
      onSaved();
    } catch (e) { setErr(e?.message || "Kayıt başarısız"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title="Faktoring İptal" size="md">
      <div className="space-y-3">
        <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs">
          <b>Uyarı:</b> Bu faktoring soft-delete edilecek. Çek durumu <b>PORTFÖYDE</b>'ye geri döner.
          {fx.status === "ÖDEME ALINDI" && (
            <div className="mt-1 text-rose-800">
              Ödeme alınmış olduğundan otomatik yazılan <b>Faktoring Gideri</b> ({money(fx.deduction)}) da iptal edilecek.
            </div>
          )}
        </div>
        <Textarea label="İptal Gerekçesi (zorunlu)" value={reason} onChange={e => setReason(e.target.value)} rows={3} data-testid="fx-cancel-reason"/>
        {err && <div className="text-xs text-rose-700" data-testid="fx-cancel-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Btn onClick={onClose} variant="ghost" data-testid="fx-cancel-back">Vazgeç</Btn>
          <Btn onClick={save} disabled={busy} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="fx-cancel-save">
            {busy ? "İşleniyor..." : "İptal Et"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}


/* iter126 — Net Çek Hareketi drill-down: portföydeki aktif çekler */
function ChequePortfolioDrillModal({ onClose, totalHint }) {
  const [cheques, setCheques] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("due_asc");
  const [bucket, setBucket] = useState("");  // iter263 — vade kova filtresi
  const [err, setErr] = useState("");
  const [actionModal, setActionModal] = useState(null); // { cheque, action }
  const [lifecycleFor, setLifecycleFor] = useState(null); // iter294h — Yaşam Döngüsü popover

  const reload = async () => {
    try {
      setBusy(true);
      const r = await api.get("/accounting/cheques/portfolio");
      setCheques(r?.cheques || r || []);
    } catch (e) { setErr(e?.response?.data?.detail || "Portföy yüklenemedi"); }
    finally { setBusy(false); }
  };

  useEffect(() => { reload(); }, []);

  const today = todayTR();
  const daysUntil = (d) => {
    if (!d) return null;
    const diff = Math.round((new Date(d.slice(0, 10)) - new Date(today)) / 86400000);
    return diff;
  };

  const filtered = useMemo(() => {
    let arr = [...cheques];
    if (q.trim()) {
      const qt = q.toLocaleLowerCase("tr");
      arr = arr.filter(c => (`${c.customer_name || ""} ${c.check_number || ""} ${c.bank_name || ""} ${c.check_company || ""}`)
        .toLocaleLowerCase("tr").includes(qt));
    }
    const sorters = {
      due_asc:  (a, b) => (a.check_due_date || "").localeCompare(b.check_due_date || ""),
      due_desc: (a, b) => (b.check_due_date || "").localeCompare(a.check_due_date || ""),
      amt_desc: (a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0),
      amt_asc:  (a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0),
      cust:     (a, b) => (a.customer_name || "").localeCompare(b.customer_name || "", "tr"),
    };
    arr.sort(sorters[sortBy] || sorters.due_asc);
    return arr;
  }, [cheques, q, sortBy]);

  const total = useMemo(() => filtered.reduce((s, c) => s + (Number(c.amount) || 0), 0), [filtered]);

  // Aging buckets by days-until-due
  const buckets = useMemo(() => {
    const b = { overdue: 0, d0_30: 0, d30_60: 0, d60_90: 0, d90p: 0 };
    for (const c of filtered) {
      const d = daysUntil(c.check_due_date);
      const amt = Number(c.amount) || 0;
      if (d === null) continue;
      if (d < 0) b.overdue += amt;
      else if (d <= 30) b.d0_30 += amt;
      else if (d <= 60) b.d30_60 += amt;
      else if (d <= 90) b.d60_90 += amt;
      else b.d90p += amt;
    }
    return b;
  }, [filtered]);

  return (
    <Modal open={true} onClose={onClose} title={`Net Çek Hareketi — Portföy Detayı (${filtered.length} çek)`} size="xl">
      <div className="space-y-3" data-testid="cheque-drill-modal">
        <div className="flex justify-end">
          {/* iter262/263 — R23 Çek Portföyü PDF trio (arama + kova + sıralama duyarlı) */}
          <PdfActions url={`/api/accounting/cheques/portfolio/pdf?sort_by=${sortBy}${q?`&q=${encodeURIComponent(q)}`:""}${bucket?`&bucket=${bucket}`:""}`} filename="Cek_Portfoyu.pdf" label="Çek Portföyü PDF" color="yellow" testidBase="muh-r23"/>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
          <div className="bg-indigo-50 border border-indigo-200 rounded p-2" data-testid="drill-total">
            <div className="text-[10px] uppercase font-semibold text-indigo-700 opacity-80">Toplam</div>
            <div className="font-mono font-bold text-indigo-900 text-sm">{money(total)}</div>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded p-2">
            <div className="text-[10px] uppercase font-semibold text-rose-700 opacity-80">Vadesi Geçmiş</div>
            <div className="font-mono font-bold text-rose-900 text-sm">{money(buckets.overdue)}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded p-2">
            <div className="text-[10px] uppercase font-semibold text-amber-700 opacity-80">0-30 gün</div>
            <div className="font-mono font-bold text-amber-900 text-sm">{money(buckets.d0_30)}</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded p-2">
            <div className="text-[10px] uppercase font-semibold text-orange-700 opacity-80">30-60 gün</div>
            <div className="font-mono font-bold text-orange-900 text-sm">{money(buckets.d30_60)}</div>
          </div>
          <div className="bg-teal-50 border border-teal-200 rounded p-2">
            <div className="text-[10px] uppercase font-semibold text-teal-700 opacity-80">60-90 gün</div>
            <div className="font-mono font-bold text-teal-900 text-sm">{money(buckets.d60_90)}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
            <div className="text-[10px] uppercase font-semibold text-emerald-700 opacity-80">90+ gün</div>
            <div className="font-mono font-bold text-emerald-900 text-sm">{money(buckets.d90p)}</div>
          </div>
        </div>

        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] uppercase text-slate-500 font-semibold block">Arama</label>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Müşteri / çek no / banka / firma"
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded" data-testid="drill-search"/>
          </div>
          <div>
            <label className="text-[10px] uppercase text-slate-500 font-semibold block">Sıralama</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="px-2 py-1.5 text-sm border border-slate-300 rounded" data-testid="drill-sort">
              <option value="due_asc">Vade (Yakın → Uzak)</option>
              <option value="due_desc">Vade (Uzak → Yakın)</option>
              <option value="amt_desc">Tutar (Büyük → Küçük)</option>
              <option value="amt_asc">Tutar (Küçük → Büyük)</option>
              <option value="cust">Müşteri (A-Z)</option>
            </select>
          </div>
        </div>

        <div className="border border-slate-200 rounded overflow-hidden max-h-[420px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="drill-table">
            <thead className="bg-slate-100 text-slate-600 uppercase text-[10px] sticky top-0">
              <tr>
                <th className="text-left px-2 py-1.5">Müşteri</th>
                <th className="text-left px-2 py-1.5">Çek No · Banka</th>
                <th className="text-left px-2 py-1.5">Çek Firması</th>
                <th className="text-left px-2 py-1.5">Vade</th>
                <th className="text-center px-2 py-1.5">Kalan</th>
                <th className="text-right px-2 py-1.5">Tutar</th>
                <th className="text-center px-2 py-1.5">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {busy && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Yükleniyor...</td></tr>}
              {!busy && filtered.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Portföyde aktif çek bulunmuyor.</td></tr>}
              {!busy && filtered.map(c => {
                const d = daysUntil(c.check_due_date);
                const overdue = d !== null && d < 0;
                const soon = d !== null && d >= 0 && d <= 7;
                return (
                  <tr key={c.id} className={`border-t border-slate-100 ${overdue ? "bg-rose-50/40" : soon ? "bg-amber-50/30" : ""}`} data-testid={`drill-row-${c.id}`}>
                    <td className="px-2 py-1.5 font-semibold text-slate-800">{c.customer_name || "—"}</td>
                    <td className="px-2 py-1.5">
                      <div className="font-mono text-slate-800">#{c.check_number || "—"}</div>
                      <div className="text-[10px] text-slate-500">{c.bank_name || "—"}</div>
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">{c.check_company || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">{(c.check_due_date || "—").slice(0, 10)}</td>
                    <td className="px-2 py-1.5 text-center">
                      {d === null ? <span className="text-slate-400">—</span>
                        : overdue ? <span className="text-[10px] font-bold text-rose-700 bg-rose-100 border border-rose-300 rounded px-1.5 py-0.5">{Math.abs(d)}g geçmiş</span>
                        : soon ? <span className="text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">{d}g</span>
                        : <span className="text-[10px] text-slate-600">{d}g</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold text-indigo-800">{money(c.amount)}</td>
                    <td className="px-2 py-1.5 text-center">
                      <div className="flex gap-1 justify-center flex-wrap">
                        <button
                          onClick={() => setActionModal({ cheque: c, action: "collect" })}
                          className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-semibold uppercase"
                          data-testid={`drill-collect-${c.id}`}
                          title="Tahsil Et — Net Nakit +Nominal"
                        >Tahsil</button>
                        <button
                          onClick={() => setActionModal({ cheque: c, action: "endorse" })}
                          className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 font-semibold uppercase"
                          data-testid={`drill-endorse-${c.id}`}
                          title="Ciro Et — Tedarikçiye devret (Net Nakit değişmez)"
                        >Ciro</button>
                        <button
                          onClick={() => setActionModal({ cheque: c, action: "factor" })}
                          className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 hover:bg-violet-200 text-violet-800 font-semibold uppercase"
                          data-testid={`drill-factor-${c.id}`}
                          title="Faktoringe Ver — Faktoring firmasına devret"
                        >Faktoring</button>
                        <button
                          onClick={() => setLifecycleFor(c)}
                          className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold uppercase flex items-center gap-0.5"
                          data-testid={`drill-lifecycle-${c.id}`}
                          title="Yaşam Döngüsü — Tüm hareket geçmişini göster"
                        ><History size={9}/>Geçmiş</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {!busy && filtered.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-300 font-bold">
                  <td colSpan={5} className="px-2 py-1.5 text-right text-slate-700">Toplam:</td>
                  <td className="px-2 py-1.5 text-right font-mono text-indigo-800">{money(total)}</td>
                  <td/>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {err && <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
        {typeof totalHint === "number" && Math.abs(total - totalHint) > 0.5 && (
          <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5" data-testid="drill-mismatch-note">
            Not: Filtre uygulanmış toplam ({money(total)}) kart üzerindeki portföy toplamıyla ({money(totalHint)}) farklı — arama filtresini temizleyerek tam toplamı görebilirsiniz.
          </div>
        )}
        <div className="flex justify-end">
          <Btn variant="secondary" onClick={onClose} data-testid="drill-close">Kapat</Btn>
        </div>
      </div>
      {actionModal && (
        <ChequeLifecycleActionModal
          cheque={actionModal.cheque}
          action={actionModal.action}
          onClose={() => setActionModal(null)}
          onDone={() => { setActionModal(null); reload(); }}
        />
      )}
      {lifecycleFor && (
        <ChequeLifecycleTimelineModal
          chequeId={lifecycleFor.id}
          onClose={() => setLifecycleFor(null)}
        />
      )}
    </Modal>
  );
}


/* iter294h — Çek Yaşam Döngüsü Timeline Modal
 * Bir çekin RECEIVED → ENDORSED/COLLECTED/FACTORED/FACTORING_SETTLED
 * olaylarını kronolojik, renk-kodlu chip'lerle gösterir. Her olay için:
 *   - Zaman, aktör, açıklama
 *   - Muhasebe etkisi (income/expense/net_cheque/net_cash delta)
 *   - Linked income/expense entry ID (audit trail)
 */
function ChequeLifecycleTimelineModal({ chequeId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/accounting/cheque/${chequeId}/lifecycle`);
        setData(r);
      } catch (e) { setErr(e?.message || "Yüklenemedi"); }
    })();
  }, [chequeId]);

  const kindStyle = (k) => ({
    RECEIVED: { bg: "bg-emerald-50 border-emerald-300", icon: "🟢", label: "Alındı" },
    ENDORSED: { bg: "bg-amber-50 border-amber-300", icon: "🟠", label: "Ciro Edildi" },
    COLLECTED: { bg: "bg-teal-50 border-teal-300", icon: "🔵", label: "Tahsil Edildi" },
    FACTORED: { bg: "bg-violet-50 border-violet-300", icon: "🟣", label: "Faktoringe Verildi" },
    FACTORING_SETTLED: { bg: "bg-purple-50 border-purple-300", icon: "💠", label: "Faktoring Ödendi" },
  }[k] || { bg: "bg-slate-50 border-slate-300", icon: "•", label: k });

  const fmtEffect = (eff = {}) => {
    const parts = [];
    if (eff.income) parts.push(`Gelir +${money(eff.income)}`);
    if (eff.expense) parts.push(`Gider +${money(eff.expense)}`);
    if (eff.net_cheque !== undefined && eff.net_cheque !== 0)
      parts.push(`Net Çek ${eff.net_cheque > 0 ? "+" : ""}${money(eff.net_cheque)}`);
    if (eff.net_cash) parts.push(`Net Nakit +${money(eff.net_cash)}`);
    return parts.join(" · ");
  };

  return (
    <Modal open={true} onClose={onClose} title="Çek Yaşam Döngüsü" size="lg">
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 mb-2">{err}</div>}
      {!data && !err && <div className="text-center py-8 text-slate-400">Yükleniyor...</div>}
      {data && (
        <div className="space-y-3">
          <div className="bg-slate-50 border rounded p-3 text-xs space-y-1">
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase font-semibold text-slate-500">Çek No</span>
              <span className="font-mono font-bold">{data.cheque.check_number || "—"}</span>
              <span className="text-[10px] uppercase font-semibold text-slate-500 ml-auto">Tutar</span>
              <span className="font-mono font-bold text-indigo-800">{money(data.cheque.amount)}</span>
            </div>
            <div className="flex items-center gap-3 text-slate-600">
              <span>🏦 {data.cheque.bank_name || "—"}</span>
              <span>Vade: <b>{(data.cheque.check_due_date || "").slice(0,10)}</b></span>
              <span>Firma: {data.cheque.check_company || "—"}</span>
            </div>
            <div className="flex items-center gap-3 text-slate-600">
              <span>👤 {data.customer?.name || "—"}</span>
              <span className="ml-auto text-[10px] uppercase font-bold rounded px-2 py-0.5 bg-indigo-100 text-indigo-800 border border-indigo-300"
                    data-testid="lifecycle-current-status">
                Mevcut Durum: {data.current_status}
              </span>
            </div>
          </div>

          <div className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-2">
            <History size={11}/> Zaman Çizelgesi ({data.events.length} olay)
          </div>
          <div className="space-y-2">
            {data.events.map((ev, idx) => {
              const s = kindStyle(ev.kind);
              return (
                <div key={idx} className={`border-l-4 rounded p-3 text-xs ${s.bg}`}
                     data-testid={`lifecycle-event-${ev.kind}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">{s.icon}</span>
                    <span className="font-bold text-slate-800">{ev.title}</span>
                    <span className="ml-auto font-mono text-[10px] text-slate-500">
                      {(ev.at || "").slice(0, 19).replace("T", " ")}
                    </span>
                  </div>
                  <div className="pl-6 space-y-1 text-slate-700">
                    <div><b>İşlem yapan:</b> {ev.actor || "—"}</div>
                    {ev.detail?.supplier_name && (
                      <div><b>Tedarikçi:</b> {ev.detail.supplier_name}</div>
                    )}
                    {ev.detail?.factoring_company && (
                      <div><b>Faktoring firması:</b> {ev.detail.factoring_company}</div>
                    )}
                    {ev.detail?.deduction > 0 && (
                      <div><b>Kesinti:</b> {money(ev.detail.deduction)} · <b>Net:</b> {money(ev.detail.net_amount)}</div>
                    )}
                    {ev.detail?.linked_expense_id && (
                      <div className="text-[10px] text-slate-500 font-mono">
                        Bağlı Gider ID: {ev.detail.linked_expense_id.slice(0, 8)}…
                      </div>
                    )}
                    {fmtEffect(ev.effect) && (
                      <div className="mt-1 inline-block bg-white/60 border border-slate-200 rounded px-2 py-0.5 text-[10px] font-mono">
                        {fmtEffect(ev.effect)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {data.events.length === 0 && (
              <div className="text-slate-400 italic text-sm text-center py-4">
                Bu çeğe ait hareket kaydı bulunamadı.
              </div>
            )}
          </div>
        </div>
      )}
      <div className="flex justify-end mt-3">
        <Btn variant="secondary" onClick={onClose} data-testid="lifecycle-close">Kapat</Btn>
      </div>
    </Modal>
  );
}


/* iter142 — Merkezi Çek Portföy Çıkışı: Tahsil / Ciro / Faktoring action modal */
function ChequeLifecycleActionModal({ cheque, action, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [date_, setDate_] = useState(todayStr());
  const [deduction, setDeduction] = useState("");
  const [note, setNote] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");

  useEffect(() => {
    if (action !== "endorse") return;
    (async () => {
      try {
        const r = await api.get("/suppliers");
        setSuppliers(r?.suppliers || r || []);
      } catch { /* non-critical */ }
    })();
  }, [action]);

  const titles = {
    collect: "Çek Tahsil Et (Vadede)",
    endorse: "Tedarikçiye Ciro Et",
    factor: "Faktoringe Ver",
  };
  const hints = {
    collect: "Net Çek −Nominal, Net Nakit +Nominal. Portföyden çıkar, DB'den silinmez.",
    endorse: "Net Çek −Nominal, Net Nakit 0. Aynı çek ID korunur.",
    factor: "Net Çek −Nominal, Net Nakit 0 (ödeme gelene kadar). Faktoring firmasından ödeme alındığında Faktoring sekmesinden 'Ödeme Alındı' işaretlenir.",
  };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      if (action === "collect") {
        await api.post(`/accounting/cheque/${cheque.id}/collect`, {
          actual_collection_date: date_, note: note || null,
        });
      } else if (action === "endorse") {
        if (!supplierId) { setErr("Tedarikçi seçin"); setBusy(false); return; }
        const sup = suppliers.find(s => s.id === supplierId);
        await api.post(`/accounting/cheque/${cheque.id}/transfer-to-supplier`, {
          transferred_to_supplier_at: date_,
          supplier_id: supplierId,
          supplier_name: sup?.name || null,
          note: note || null,
        });
      } else if (action === "factor") {
        const ded = Number(String(deduction || "0").replace(",", ".")) || 0;
        if (ded < 0) { setErr("Kesinti negatif olamaz"); setBusy(false); return; }
        if (ded > Number(cheque.amount || 0) + 0.005) {
          setErr("Kesinti çek tutarından büyük olamaz"); setBusy(false); return;
        }
        await api.post(`/accounting/factoring`, {
          cheque_id: cheque.id,
          factoring_date: date_,
          deduction: ded,
          note: note || null,
        });
      }
      onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "İşlem başarısız");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title={titles[action]} size="md">
      <div className="space-y-3" data-testid={`cheque-action-modal-${action}`}>
        <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
          <div><b>Müşteri:</b> {cheque.customer_name || "—"}</div>
          <div><b>Çek No · Banka:</b> #{cheque.check_number} · {cheque.bank_name}</div>
          <div><b>Tutar:</b> <span className="font-mono">{money(cheque.amount)}</span></div>
          <div><b>Vade:</b> <span className="font-mono">{(cheque.check_due_date || "—").slice(0, 10)}</span></div>
        </div>
        <div className="text-[11px] text-slate-600 bg-indigo-50 border border-indigo-200 rounded p-2">{hints[action]}</div>

        <Input
          type="date"
          label={action === "collect" ? "Tahsilat Tarihi" : action === "endorse" ? "Ciro Tarihi" : "Faktoring Tarihi"}
          value={date_}
          onChange={e => setDate_(e.target.value)}
          data-testid={`cheque-action-date-${action}`}
        />

        {action === "endorse" && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pl-0.5">Tedarikçi *</label>
            <select
              value={supplierId}
              onChange={e => setSupplierId(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
              data-testid="cheque-action-supplier"
            >
              <option value="">-- Tedarikçi seçin --</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}

        {action === "factor" && (
          <Input
            type="number"
            label="Faktoring Kesintisi (₺)"
            value={deduction}
            onChange={e => setDeduction(e.target.value)}
            placeholder="0"
            data-testid="cheque-action-deduction"
          />
        )}

        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pl-0.5">Not (opsiyonel)</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
            data-testid={`cheque-action-note-${action}`}
          />
        </div>

        {err && <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded p-2" data-testid="cheque-action-error">{err}</div>}

        <div className="flex gap-2 justify-end">
          <Btn variant="secondary" onClick={onClose} data-testid={`cheque-action-cancel-${action}`}>Vazgeç</Btn>
          <Btn
            onClick={submit}
            disabled={busy}
            className={
              action === "collect" ? "bg-emerald-600 text-white hover:bg-emerald-700" :
              action === "endorse" ? "bg-amber-600 text-white hover:bg-amber-700" :
              "bg-violet-600 text-white hover:bg-violet-700"
            }
            data-testid={`cheque-action-submit-${action}`}
          >
            {busy ? "İşleniyor..." : "Onayla"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}


/* RequestsPage — re-export original so /requests route keeps working */
export { RequestsPage } from "./Finance";
